import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { useBonsai } from "../contexts/BonsaiContext";
import TestModeToggle, { TestModeBanner } from "../components/TestModeToggle";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  User, 
  Users, 
  Building2,
  FileText,
  ArrowLeft,
  Loader2,
  Info,
  ExternalLink,
  Save,
  Download,
  ClipboardCheck,
  Clock,
  ChevronRight,
  Mail,
  FolderUp,
  Scale,
  Send,
  RefreshCw,
  X
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

import { API } from '../config/api';
import { confirmSave } from "../utils/confirmSave";

// BM-H4487: the one "Still saving" notice while a lost answer is confirmed.
const SAVE_CONFIRM_TOAST_ID = "conflict-save-confirm";
import {
  conflictDecisionLabel,
  canSendObForConflict,
  isClientRecordMatch,
  ownClientConfirmationPayload,
  ownClientConfirmationsValid,
  everyMatchConfirmedOwnClient,
  OWN_CLIENT_MIN_REASON,
  DIRECTOR_MIN_REASON,
  directorApprovalStatus,
  forfStageReady,
  forfPath,
} from '../lib/conflictDecision';
import { PRACTICE_AREAS } from '../config/constants';
// FEE_EARNERS is the canonical list used by the Fee Earner dropdown.
// Without this import the page crashes at render with a ReferenceError
// — surfaced via Vanessa/Sangita 13 May 2026 hotfix.
import { FEE_EARNERS, DECISION_MAKERS } from '../config/roleConfig';
import { STAFF } from '../config/staffConfig';

// BM-H3332: the onboarding panel used to ask for the fee earner's email
// again after the conflict check had already named the fee earner.
export function staffEmailFor(name) {
  const n = (name || "").trim().toLowerCase();
  if (!n) return "";
  const row = STAFF.find((s) =>
    (s.full_name || "").toLowerCase() === n ||
    (s.initials || "").toLowerCase() === n ||
    (s.aliases || []).some((a) => (a || "").toLowerCase() === n));
  return (row && row.email) || "";
}
import { uncoveredJointClients, uncoveredMessage } from "../utils/jointCoverage";
import DeferredConflictChecks from "../components/DeferredConflictChecks";

// 14May2026: prefer window.errorMessage(err, fallback) so backend HTTPException
// details surface verbatim. Never render an Error object as a React child and
// never swallow the cause with a generic string.
function _errorMessage(err, fallback) {
  return errorMessage(err, fallback);
}

// DECISION_MAKERS is imported from roleConfig (Brief 45) — the canonical,
// staffConfig-derived list — so this page no longer carries a hardcoded
// four-name list that silently drifted from the roster (Sharon Mutizira
// was missing, and the list could not track future staff changes).

if (!Array.isArray(FEE_EARNERS) || FEE_EARNERS.length === 0) {
  // Loud failure — never silently fall back. A broken FEE_EARNERS export
  // means the staff config is wrong and every conflict check would be
  // recorded against an empty fee-earner list. Surface immediately.
  throw new Error(
    "FEE_EARNERS is not defined or empty — check config/roleConfig.js + staffConfig.js"
  );
}

if (!Array.isArray(DECISION_MAKERS) || DECISION_MAKERS.length === 0) {
  // Loud failure — never silently fall back. An empty DECISION_MAKERS
  // export means no clearance could be assigned a decision maker, which
  // would block every conflict-check approval. Surface immediately.
  throw new Error(
    "DECISION_MAKERS is not defined or empty — check config/roleConfig.js + staffConfig.js"
  );
}

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// BM-H615: exported so its quote seeding can be rendered in a test.
// initialClientEmail / initialMatterDescription come from a quote link
// (BM-H77 Phase-2) and seed the onboarding form; the fee earner can edit them.
export function NextStepsPanel({
  savedConflictCheck, setSavedConflictCheck, navigate, onReset,
  initialClientEmail = "", initialMatterDescription = "",
}) {
  const [matterRef, setMatterRef] = useState("");
  const [filing, setFiling] = useState(false);
  const [filingResult, setFilingResult] = useState(null);

  // Stage 2: OB state
  const [clientEmail, setClientEmail] = useState(initialClientEmail || "");
  // BM-H3332: start from what the conflict check already holds rather
  // than asking for the fee earner and description a second time.
  const [feeEarner, setFeeEarner] = useState(savedConflictCheck.fee_earner || "");
  // Stage B1: fee_earner_email + matter_description are now required by
  // the OB-send endpoint. Capture them here.
  const [feeEarnerEmail, setFeeEarnerEmail] = useState(
    () => savedConflictCheck.fee_earner_email || staffEmailFor(savedConflictCheck.fee_earner));
  const [matterDescription, setMatterDescription] = useState(
    initialMatterDescription || savedConflictCheck.brief_description || "");
  const [sendingOB, setSendingOB] = useState(false);
  const [markingThirdfortComplete, setMarkingThirdfortComplete] = useState(false);
  const [obResult, setObResult] = useState(null);
  const [obStatus, setObStatus] = useState(null); // from status API
  const [obOverridden, setObOverridden] = useState(false);

  // Joint clients for OB (pre-populated from conflict check)
  const [jointClientEmails, setJointClientEmails] = useState(
    () => (savedConflictCheck.joint_clients || []).map(jc => ({ name: jc.name, email: jc.email || "", mobile: jc.mobile || "" }))
  );

  // Stage 3: Thirdfort state
  const [thirdfortStatus, setThirdfortStatus] = useState("pending"); // pending | sent | complete | overridden
  // BM-H3324: AML Policy §7.5, only a director may override onboarding.

  // Phase 2b/2c: Thirdfort report-review sub-stages.
  // Sourced from /onboarding/status/{ref} (full OB doc). Updated locally
  // after each successful tick so the UI reflects state without a reload.
  const [reportReceivedAt, setReportReceivedAt] = useState(null);
  const [reportReceivedBy, setReportReceivedBy] = useState(null);
  const [reportCheckedAt, setReportCheckedAt] = useState(null);
  const [reportCheckedBy, setReportCheckedBy] = useState(null);
  const [reportOutcome, setReportOutcome] = useState(null);
  const [markingReportReceived, setMarkingReportReceived] = useState(false);
  const [markingReportChecked, setMarkingReportChecked] = useState(false);
  // R&D-E Gap 5 — Thirdfort report PDF is now required to tick "checked".
  // BM-H274: the fee earner review is a mini risk assessment, so the panel
  // carries the affirmation and the outcome, not just a file.
  const [reviewAffirmed, setReviewAffirmed] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewOutcome, setReviewOutcome] = useState("approved");
  const [reportReviewedBy, setReportReviewedBy] = useState(null);
  const [reportReviewedAt, setReportReviewedAt] = useState(null);

  const conflictStatus = savedConflictCheck.conflict_status || "clear";
  const isNA = conflictStatus === "na";
  const isNotReady = conflictStatus === "not_ready";
  const savedRef = savedConflictCheck.matter_reference || savedConflictCheck.provisional_id;
  // Issue #2959: an existing client's search always hits their own earlier
  // matter, so the save goes to supervisor approval. This panel used to call
  // that "Cleared" and offer Send OB, which the backend then refused with a
  // 400. Offer the send only from a status the backend will accept.
  const isPendingApproval = conflictStatus === "pending_approval";
  const obSendable = canSendObForConflict(savedConflictCheck);
  const [refreshingApproval, setRefreshingApproval] = useState(false);

  // Issue #2959: re-read the check so the OB form appears once the
  // supervisor has approved, without the fee earner leaving this panel.
  // The approval also mints the matter reference, which this picks up.
  const handleRefreshApproval = async () => {
    setRefreshingApproval(true);
    try {
      const res = await axios.get(`${API}/conflict-checks/${savedConflictCheck.id}`);
      const fresh = res.data || {};
      setSavedConflictCheck((prev) => ({ ...prev, ...fresh }));
      if (canSendObForConflict(fresh)) {
        toast.success("Conflict check approved. You can now send onboarding.");
      } else {
        toast.info(`Still awaiting approval (status: ${fresh.conflict_status || "not recorded"}).`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Could not re-check the conflict check"));
    } finally {
      setRefreshingApproval(false);
    }
  };

  // Check OB status on mount if we have a matter reference
  useEffect(() => {
    if (savedRef) {
      checkObStatus();
    }
  }, [savedRef]);

  const checkObStatus = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status/${savedRef}`);
      setObStatus(res.data);
      if (res.data.status === "complete" || res.data.status === "overridden") {
        setObOverridden(res.data.status === "overridden");
        setObResult({ matter_reference: savedRef, status: res.data.status });
      } else if (res.data.status === "not_sent" || res.data.status === "invited") {
        setObResult({ matter_reference: savedRef, status: res.data.status });
      }
      // Check thirdfort status if available
      if (res.data.thirdfort_status) {
        setThirdfortStatus(res.data.thirdfort_status);
      }
      // Phase 2b/2c — report-review sub-stage state.
      setReportReceivedAt(res.data.thirdfort_report_received_at || null);
      setReportReceivedBy(res.data.thirdfort_report_received_by || null);
      setReportCheckedAt(res.data.thirdfort_report_checked_at || null);
      setReportCheckedBy(res.data.thirdfort_report_checked_by || null);
      setReportOutcome(res.data.thirdfort_report_outcome || null);
      // BM-H274 fee earner review record.
      setReportReviewedAt(res.data.thirdfort_report_reviewed_at || null);
      setReportReviewedBy(res.data.thirdfort_report_reviewed_by || null);
      setReviewNotes(res.data.thirdfort_report_review_notes || "");
    } catch {
      // No onboarding record yet — that's fine
    }
  };

  const handleSendOB = async () => {
    if (!clientEmail.trim()) {
      toast.error("Client email is required to send onboarding");
      return;
    }
    // Validate joint client emails if any joint clients exist
    const validJointClients = jointClientEmails.filter(jc => jc.name.trim());
    for (const jc of validJointClients) {
      if (!jc.email.trim()) {
        toast.error(`Email is required for joint client "${jc.name}"`);
        return;
      }
      // Brief 37: mobile is optional but if provided must be a UK number,
      // because the admin team uses it to dispatch Thirdfort manually.
      if (jc.mobile && jc.mobile.trim()) {
        const m = jc.mobile.replace(/\s+/g, "");
        if (!/^(\+?44|0)7\d{9}$/.test(m)) {
          toast.error(`Mobile for "${jc.name}" must be a valid UK mobile (or left blank)`);
          return;
        }
      }
    }
    setSendingOB(true);
    try {
      // Stage A1: read client_type from the saved conflict check directly —
      // no silent "Private individual" default. The conflict-clearance form
      // already 422s on missing client_type, so by the time we reach OB-send
      // it must be present.
      //
      // Defensive refetch (Liezel's 13 May 2026 false-error bug): if
      // savedConflictCheck.client_type is missing it is far more likely
      // that the POST /conflict-checks response omitted the field than that
      // the user actually submitted without it (the clearance gate 422s on
      // empty client_type, so by the time we have a savedConflictCheck.id
      // the row WAS written with a client_type). Refetch the full doc from
      // the server before falsely accusing the user of needing to re-record.
      let effectiveClientType = savedConflictCheck.client_type || "";
      if (!effectiveClientType) {
        try {
          const refetch = await axios.get(
            `${API}/conflict-checks/${savedConflictCheck.id}`,
          );
          effectiveClientType = refetch.data?.client_type || "";
          if (effectiveClientType && typeof setSavedConflictCheck === "function") {
            // Surface the fresh fields back up to the parent so the rest
            // of the panel sees the same state on its next render.
            setSavedConflictCheck((prev) => ({ ...prev, ...refetch.data }));
          }
        } catch (refetchErr) {
          // Surface the refetch failure to the console for diagnosis but
          // fall through to the error path below — we will not silently
          // send OB without a confirmed client_type.
          console.error(
            "Refetch of conflict check failed while checking client_type:",
            refetchErr,
          );
        }
        if (!effectiveClientType) {
          // Both the cached value AND the freshly-fetched doc lack
          // client_type. That is a real data error, not user error —
          // the wording must reflect that.
          toast.error(
            "Conflict check is missing client type — this is a data error, please contact support.",
          );
          setSendingOB(false);
          return;
        }
      }
      // Stage B1: matter_description (>=20 chars) and fee_earner_email are
      // both now required by the backend. Surface the requirement here so
      // the FE collects them rather than 422ing on send.
      if (!matterDescription.trim() || matterDescription.trim().length < 20) {
        toast.error("Matter description (min 20 characters) is required to send onboarding.");
        setSendingOB(false);
        return;
      }
      if (!feeEarnerEmail.trim()) {
        toast.error("Fee earner email is required to send onboarding.");
        setSendingOB(false);
        return;
      }
      const res = await axios.post(`${API}/onboarding/send-from-conflict-check`, {
        conflict_check_id: savedConflictCheck.id,
        client_name: savedConflictCheck.client_name || "",
        client_email: clientEmail.trim(),
        client_type: effectiveClientType,
        fee_earner: feeEarner,
        fee_earner_email: feeEarnerEmail.trim(),
        matter_description: matterDescription.trim(),
        joint_clients: validJointClients.map(jc => ({ name: jc.name.trim(), email: jc.email.trim(), mobile: (jc.mobile || "").trim() })),
      });
      setObResult(res.data);
      if (res.data.status === "already_sent") {
        toast.info(`Onboarding already sent (${res.data.matter_reference || res.data.provisional_id})`);
      } else {
        const jcMsg = validJointClients.length > 0 ? ` + ${validJointClients.length} joint client(s)` : "";
        toast.success(`Onboarding link sent to ${clientEmail}${jcMsg}`);
      }
    } catch (e) {
      console.error("Failed to send onboarding:", e);
      toast.error(errorMessage(e, "Failed to send onboarding link"));
    } finally {
      setSendingOB(false);
    }
  };

  const handleMarkThirdfortComplete = async () => {
    // Stage A3: persist to the backend instead of mutating React state
    // only. Surface failures with the actual backend error — no false
    // success toast.
    if (!savedRef) {
      toast.error("No matter reference for this conflict check.");
      return;
    }
    setMarkingThirdfortComplete(true);
    try {
      await axios.post(`${API}/onboarding/${encodeURIComponent(savedRef)}/thirdfort-complete`, {});
      setThirdfortStatus("complete");
      toast.success("Thirdfort/AML marked as complete");
    } catch (e) {
      console.error("Failed to mark Thirdfort complete:", e);
      toast.error(errorMessage(e, "Failed to mark Thirdfort complete"));
    } finally {
      setMarkingThirdfortComplete(false);
    }
  };

  // Phase 2b — ClientCare ticks "Thirdfort report received".
  const handleMarkReportReceived = async () => {
    if (!savedRef) {
      toast.error("No matter reference for this conflict check.");
      return;
    }
    setMarkingReportReceived(true);
    try {
      const res = await axios.post(
        `${API}/onboarding/${encodeURIComponent(savedRef)}/thirdfort-report-received`,
        {},
      );
      setReportReceivedAt(res.data.thirdfort_report_received_at);
      setReportReceivedBy(res.data.thirdfort_report_received_by);
      // Mirror PR #406 warning-field pattern — surface notify_status
      // truthfully rather than show a false-success toast when SMTP fails.
      // BM-H273: only claim the fee earner was notified when the backend
      // actually resolved them. An unresolved fee earner routes to the
      // shared mailbox, and calling that "fee earner notified" is a lie.
      if (res.data.notify_status === "logged") {
        const who = (res.data.notified_recipients || []).join(", ");
        toast.success(
          who
            ? `Thirdfort report marked as received. Notified ${who}`
            : "Thirdfort report marked as received. Fee earner notified",
        );
      } else if (res.data.notify_status === "logged_fee_earner_unresolved") {
        toast.warning(
          "Marked received, but the fee earner for this matter could not be " +
          "identified, so the notification went to the shared mailbox. Set the " +
          "fee earner on the matter and tell them directly.",
        );
      } else if (res.data.already_recorded) {
        toast.info("Thirdfort report was already marked as received");
      } else {
        toast.warning(`Marked received, but notification status: ${res.data.notify_status || "unknown"}`);
      }
    } catch (e) {
      console.error("Failed to mark Thirdfort report received:", e);
      toast.error(errorMessage(e, "Failed to mark Thirdfort report received"));
    } finally {
      setMarkingReportReceived(false);
    }
  };

  // Phase 2c — Fee earner ticks "report checked, all good".
  // BM-H274 (Vanessa): "the fee earner must read and approve the Thirdfort
  // report like a mini risk assessment." One handler, three outcomes. It is
  // the approval that satisfies AML, never the receipt, and refusing is a
  // first-class path rather than a tick nobody presses.
  const handleSubmitReportReview = async (outcome) => {
    if (!savedRef) {
      toast.error("No matter reference for this conflict check.");
      return;
    }
    const isApproval = outcome === "approved";
    if (!reportCheckedAt) {
      toast.error(
        "Record the first review from the AML panel on the matter, where the "
        + "Thirdfort report filed in SharePoint can be selected.",
      );
      return;
    }
    if (isApproval && !reviewAffirmed) {
      toast.error("Confirm you have read the Thirdfort report before approving.");
      return;
    }
    if (!isApproval && !reviewNotes.trim()) {
      toast.error("Give a reason when you do not approve the report.");
      return;
    }
    setMarkingReportChecked(true);
    try {
      const form = new FormData();
      form.append("outcome", outcome);
      form.append("affirm_read", isApproval ? "true" : "false");
      if (reviewNotes.trim()) form.append("notes", reviewNotes.trim());
      const res = await axios.post(
        `${API}/onboarding/${encodeURIComponent(savedRef)}/thirdfort-report-checked`,
        form,
      );
      setReportOutcome(res.data.thirdfort_report_outcome);
      setReportReviewedAt(res.data.thirdfort_report_reviewed_at);
      setReportReviewedBy(res.data.thirdfort_report_reviewed_by);
      setReportCheckedAt(res.data.thirdfort_report_checked_at);
      setReportCheckedBy(res.data.thirdfort_report_checked_by);
      setReviewAffirmed(false);
      if (res.data.already_recorded) {
        toast.info("This report has already been approved");
      } else if (res.data.aml_satisfied) {
        // BM-H273: report the stage the approval actually moved the matter
        // to, so an approval that fails to move it is visible.
        toast.success(
          res.data.pipeline_stage
            ? `Report approved. AML satisfied, matter now at ${res.data.pipeline_stage}`
            : "Report approved. AML satisfied, cashier notified to add trust account",
        );
      } else {
        // Never a silent no-op: say where the matter now sits.
        toast.warning(
          `Report not approved (${res.data.thirdfort_report_outcome}). ` +
          "AML stays outstanding, the matter is held at the AML check and " +
          "the admin team have been notified.",
        );
      }
    } catch (e) {
      console.error("Failed to record Thirdfort report review:", e);
      toast.error(errorMessage(e, "Failed to record the Thirdfort report review"));
    } finally {
      setMarkingReportChecked(false);
    }
  };

  const obDone = obResult && (obResult.status === "complete" || obResult.status === "overridden" || obResult.status === "skipped" || obResult.status === "manually_completed" || obOverridden);
  const obSent = !!obResult;
  // BM-H274: "complete" is Stage C, the client finished the Thirdfort link.
  // It no longer counts as done, because the fee earner still has to read and
  // approve the report. "approved" is what the approval writes.
  const thirdfortDone = thirdfortStatus === "approved" || thirdfortStatus === "overridden";
  const reportApproved = reportOutcome === "approved" || reportOutcome === "all_good";
  const reportDeclined = reportOutcome === "rejected" || reportOutcome === "referred" || reportOutcome === "concerns";
  // Pipeline walkthrough 24 Sep 2026: the FORF follows onboarding RETURNED
  // (obDone), not merely sent, and AML approved. See forfStageReady.
  const canProceedToFORF = forfStageReady({ obDone, thirdfortDone, matterRef: savedRef });

  const handleFileToSharePoint = async () => {
    if (!matterRef.trim()) {
      toast.error("Enter a matter reference to file to");
      return;
    }
    setFiling(true);
    try {
      const res = await axios.post(
        `${API}/conflict-checks/${savedConflictCheck.id}/file-to-sharepoint`,
        { matter_reference: matterRef.trim() }
      );
      if (res.data.success) {
        toast.success(`Filed to ${matterRef.trim()}/Compliance`);
        setFilingResult(res.data);
      } else {
        toast.error(res.data.message || "Filing failed");
      }
    } catch (e) {
      console.error("Filing failed:", e);
      toast.error(errorMessage(e, "Filing failed"));
    } finally {
      setFiling(false);
    }
  };

  // BM-H613 (25 Sep 2026): completing a deferred check does not open a
  // matter. The matter is already open under its reference; there is no
  // onboarding to send and no new folder. Say what happened and stop.
  if (savedConflictCheck.completed_deferred) {
    return (
      <div className="glass-card rounded-md p-5 mb-6 border border-teal/40" data-testid="deferred-completed-panel">
        <h3 className="text-base font-semibold text-white mb-1">Deferred conflict check completed</h3>
        <p className="text-sm text-txt-muted mb-3">
          {savedConflictCheck.message ||
            `Completed on ${savedRef}: ${conflictStatus.replace(/_/g, " ")}`}
        </p>
        <div className="flex gap-2">
          <Button size="sm" className="bg-teal hover:bg-teal text-white"
            onClick={() => navigate(`/conflict-check/${savedConflictCheck.id}`)}
            data-testid="deferred-completed-open-check">
            Open the conflict check
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-teal bg-teal-dim/30/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-teal" />
          Conflict Check Recorded — Next Steps
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-txt-muted mb-1">
          Matter Reference: <strong data-testid="next-steps-matter-reference">{savedRef || "Generating..."}</strong>
        </p>
        <p className="text-sm text-slate-500 mb-1">
          Follow the Golden Rule workflow: <strong>Conflict</strong> &rarr; <strong>OB</strong> &rarr; <strong>Thirdfort</strong> &rarr; <strong>FORF</strong>
        </p>
        <p className="text-sm text-txt-muted mb-4">
          The conflict check has been saved to SharePoint. Complete the steps below in order:
        </p>

        <div className="space-y-3">
          {/* ---- Stage 1: Conflict ---- */}
          <div className={`p-3 ${isNotReady ? 'bg-status-amber-dim/30' : isNA ? 'bg-blue-900/30' : 'bg-teal-dim/30'} rounded-md border ${isNotReady ? 'border-amber-500/40' : isNA ? 'border-blue-500/40' : 'border-teal/40'}`} data-testid="stage-1-conflict-done">
            <div className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-md ${isNotReady ? 'bg-status-amber' : isNA ? 'bg-blue-600' : 'bg-teal'} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>1</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className={`text-sm font-semibold ${isNotReady ? 'text-status-amber' : isNA ? 'text-blue-300' : 'text-teal'}`}>Conflict Check</h4>
                  {isNA && (
                    <Badge className="bg-blue-100 text-blue-300 border-blue-200 text-[10px]">
                      N/A — Advice Only
                    </Badge>
                  )}
                  {isNotReady && (
                    <Badge className="bg-status-amber-dim/30 text-status-amber border-amber-500/40 text-[10px]">
                      <Clock className="w-3 h-3 mr-0.5" /> Deferred
                    </Badge>
                  )}
                  {isPendingApproval && (
                    <Badge className="bg-status-amber-dim/30 text-status-amber border-amber-500/40 text-[10px]">
                      <Clock className="w-3 h-3 mr-0.5" /> Awaiting approval
                    </Badge>
                  )}
                  {!isNA && !isNotReady && !isPendingApproval && (
                    <Badge className="bg-teal-dim text-teal border-teal text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> Complete
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {isNA 
                    ? `Advice-only matter for "${savedConflictCheck.client_name}" — no other side to check`
                    : isNotReady 
                      ? `Deferred for "${savedConflictCheck.client_name}" — ${savedConflictCheck.not_ready_reason || "will run later"}`
                      : isPendingApproval
                        ? `Awaiting supervisor approval for "${savedConflictCheck.client_name}"${savedConflictCheck.approval_supervisor ? ` (sent to ${savedConflictCheck.approval_supervisor})` : ""}`
                        : `Cleared for "${savedConflictCheck.client_name}"`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* ---- Stage 2: Client Onboarding (OB) ---- */}
          <div className={`p-4 bg-[rgba(26,37,71,0.7)] rounded-md border ${obSent ? 'border-teal/40' : 'border-blue-500/40'} transition-colors`} data-testid="stage-2-ob">
            <div className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-md ${obSent ? 'bg-teal' : 'bg-blue-600'} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>2</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-white">Client Onboarding (OB)</h4>
                  {obDone && (
                    <Badge className="bg-teal-dim text-teal border-teal text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> {obOverridden ? "Overridden" : "Complete"}
                    </Badge>
                  )}
                  {obSent && !obDone && (
                    <Badge className="bg-blue-100 text-blue-300 border-blue-200 text-[10px]">
                      <Clock className="w-3 h-3 mr-0.5" /> Sent — awaiting client
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {obSent
                    ? "Onboarding link has been sent. Waiting for client to complete TOB acceptance and details."
                    : "Send the client onboarding form (TOB + details) to the client before proceeding."
                  }
                </p>

                {!obSent && !obSendable ? (
                  <div className="space-y-2" data-testid="ob-awaiting-conflict-approval">
                    <p className="text-xs text-status-amber">
                      {isPendingApproval
                        ? "Onboarding can be sent once the supervisor has approved this conflict check."
                        : `Onboarding cannot be sent until this conflict check is cleared (current status: ${savedConflictCheck.conflict_status || "not recorded"}).`}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRefreshApproval}
                      disabled={refreshingApproval}
                      className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white text-xs"
                      data-testid="ob-refresh-approval-btn"
                    >
                      {refreshingApproval ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                      Check approval
                    </Button>
                  </div>
                ) : !obSent ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Primary Client Email *</label>
                        <input
                          type="email"
                          value={clientEmail}
                          onChange={(e) => setClientEmail(e.target.value)}
                          placeholder="client@example.com"
                          className="w-full mt-0.5 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                          data-testid="ob-client-email-input"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Fee Earner *</label>
                        <input
                          type="text"
                          value={feeEarner}
                          onChange={(e) => setFeeEarner(e.target.value)}
                          placeholder="Fee earner name"
                          className="w-full mt-0.5 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                          data-testid="ob-fee-earner-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Fee Earner Email *</label>
                      <input
                        type="email"
                        value={feeEarnerEmail}
                        onChange={(e) => setFeeEarnerEmail(e.target.value)}
                        placeholder="fee.earner@bonsai.law"
                        className="w-full mt-0.5 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                        data-testid="ob-fee-earner-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Matter description * (min 20 chars)</label>
                      <textarea
                        value={matterDescription}
                        onChange={(e) => setMatterDescription(e.target.value)}
                        placeholder="Short description of the matter and scope (20-1000 chars)"
                        rows={2}
                        minLength={20}
                        maxLength={1000}
                        className="w-full mt-0.5 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                        data-testid="ob-matter-description-input"
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5">{matterDescription.length}/1000 chars</p>
                    </div>
                    {/* Joint Client OB Emails */}
                    {jointClientEmails.length > 0 && (
                      <div className="space-y-1.5 p-2.5 bg-blue-900/20 rounded-md border border-blue-500/20">
                        <p className="text-[10px] font-medium text-blue-400 uppercase tracking-wider flex items-center gap-1">
                          <Users className="w-3 h-3" /> Joint Client Onboarding — each will receive a separate TOB + Thirdfort link
                        </p>
                        {jointClientEmails.map((jc, idx) => (
                          <div key={idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                            <span className="text-xs text-blue-300 font-medium w-32 truncate" title={jc.name}>{jc.name}</span>
                            <input
                              type="email"
                              value={jc.email}
                              onChange={(e) => {
                                const updated = [...jointClientEmails];
                                updated[idx] = { ...updated[idx], email: e.target.value };
                                setJointClientEmails(updated);
                              }}
                              placeholder={`${jc.name}'s email`}
                              className="flex-1 min-w-[140px] px-3 py-1.5 text-xs border border-blue-500/30 rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                              data-testid={`ob-joint-email-${idx}`}
                            />
                            {/* Brief 37: mobile capture for manual Thirdfort dispatch by admin */}
                            <input
                              type="tel"
                              value={jc.mobile || ""}
                              onChange={(e) => {
                                const updated = [...jointClientEmails];
                                updated[idx] = { ...updated[idx], mobile: e.target.value };
                                setJointClientEmails(updated);
                              }}
                              placeholder="Mobile (for Thirdfort)"
                              className="flex-1 min-w-[140px] px-3 py-1.5 text-xs border border-blue-500/30 rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                              data-testid={`ob-joint-mobile-${idx}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={handleSendOB}
                      disabled={
                        sendingOB ||
                        !clientEmail.trim() ||
                        !feeEarner.trim() ||
                        !feeEarnerEmail.trim() ||
                        matterDescription.trim().length < 20
                      }
                      className="bg-blue-600 hover:bg-blue-700 text-xs"
                      data-testid="send-ob-btn"
                    >
                      {sendingOB ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                      Send Onboarding{jointClientEmails.length > 0 ? ` to ${1 + jointClientEmails.length} clients` : ""}
                    </Button>
                  </div>
                ) : !obDone ? (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={checkObStatus} className="text-xs border-blue-500/40 text-blue-700" data-testid="refresh-ob-status-btn">
                      <Loader2 className="w-3 h-3 mr-1" /> Refresh Status
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* ---- Stage 3: Thirdfort / AML ---- */}
          <div className={`p-4 bg-[rgba(26,37,71,0.7)] rounded-md border ${thirdfortDone ? 'border-teal/40' : obSent ? 'border-amber-500/40' : 'border-navy-border'} transition-colors ${!obSent ? 'opacity-60' : ''}`} data-testid="stage-3-thirdfort">
            <div className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-md ${thirdfortDone ? 'bg-teal' : obSent ? 'bg-status-amber' : 'bg-slate-400'} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>3</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-white">Thirdfort / AML Checks</h4>
                  {thirdfortDone && (
                    <Badge className="bg-teal-dim text-teal border-teal text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> {thirdfortStatus === "overridden" ? "Overridden" : "Complete"}
                    </Badge>
                  )}
                  {!thirdfortDone && !obSent && (
                    <Badge className="bg-slate-100 text-slate-500 border-navy-border text-[10px]">
                      Waiting for OB
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {thirdfortDone
                    ? "Thirdfort/AML checks have been completed or overridden."
                    : obSent
                      ? `Issue Thirdfort checks for ${jointClientEmails.length > 0 ? `all ${1 + jointClientEmails.length} clients` : "the client"} using OB data. Mark complete when done.`
                      : "Complete Stage 2 (Client Onboarding) first before issuing Thirdfort checks."
                  }
                </p>

                {obSent && !thirdfortDone && (
                  <div className="space-y-2">
                    {/* Show all clients needing Thirdfort */}
                    {jointClientEmails.length > 0 && (
                      <div className="p-2 bg-status-amber-dim/20 rounded-md border border-amber-500/20 mb-2">
                        <p className="text-[10px] font-medium text-status-amber uppercase tracking-wider mb-1">Thirdfort required for:</p>
                        <div className="space-y-0.5">
                          <p className="text-xs text-white">1. {savedConflictCheck.client_name} <span className="text-status-amber/60">(primary)</span></p>
                          {jointClientEmails.map((jc, idx) => (
                            <p key={idx} className="text-xs text-white">{idx + 2}. {jc.name} <span className="text-blue-300/60">(joint)</span></p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={handleMarkThirdfortComplete} disabled={markingThirdfortComplete} className="bg-teal hover:bg-teal text-xs" data-testid="mark-thirdfort-complete-btn">
                        {markingThirdfortComplete ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                        {markingThirdfortComplete ? "Marking..." : "Mark as Complete"}
                      </Button>
                    </div>
                    {/* BM-H3324: the Thirdfort "Override" that lived here changed only this
                        page and recorded nothing. Proceeding without an approved Thirdfort
                        report is a director's decision (AML Policy §7.5), made with "Confirm
                        AML" on the pipeline, which is recorded and audited. */}
                    <p className="text-xs text-slate-400" data-testid="thirdfort-override-moved">
                      To proceed without an approved Thirdfort report, a director must confirm the AML check on the pipeline.
                    </p>
                  </div>
                )}

                {/* Phase 2b — Thirdfort report received tick (visible once Stage C is complete) */}
                {thirdfortStatus === "complete" && (
                  <div className="mt-3 pt-3 border-t border-navy-border space-y-2" data-testid="stage-3d-report-received">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-white">Thirdfort report received</p>
                        <p className="text-[11px] text-slate-400">
                          {reportReceivedAt
                            ? `Ticked ${new Date(reportReceivedAt).toLocaleString()}${reportReceivedBy ? ` by ${reportReceivedBy}` : ""}`
                            : "ClientCare ticks once the Thirdfort report has been received from the provider."}
                        </p>
                      </div>
                      {reportReceivedAt ? (
                        <Badge className="bg-teal-dim text-teal border-teal text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-0.5" /> Received
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={handleMarkReportReceived}
                          disabled={markingReportReceived}
                          className="bg-teal hover:bg-teal text-xs"
                          data-testid="mark-report-received-btn"
                        >
                          {markingReportReceived ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                          {markingReportReceived ? "Marking..." : "Mark received"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* BM-H274: fee earner review of the Thirdfort report. Not a
                    tick: a mini risk assessment. Approve satisfies AML and
                    advances the matter; not approving holds it at the AML
                    check and tells the admin team why. */}
                {reportReceivedAt && (
                  <div className="mt-3 pt-3 border-t border-navy-border space-y-2" data-testid="stage-3e-report-checked">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-white">Fee earner review of the Thirdfort report</p>
                        <p className="text-[11px] text-slate-400">
                          {reportReviewedAt || reportCheckedAt
                            ? `${reportApproved ? "Approved" : "Reviewed"} ${new Date(reportReviewedAt || reportCheckedAt).toLocaleString()}${(reportReviewedBy || reportCheckedBy) ? ` by ${reportReviewedBy || reportCheckedBy}` : ""}${reportOutcome ? `, outcome: ${reportOutcome}` : ""}`
                            : "The fee earner reads the Thirdfort report and rules on it. AML is not satisfied until they approve it."}
                        </p>
                      </div>
                      {reportApproved ? (
                        <Badge className="bg-teal-dim text-teal border-teal text-[10px]" data-testid="report-approved-badge">
                          <CheckCircle2 className="w-3 h-3 mr-0.5" /> Approved
                        </Badge>
                      ) : reportDeclined ? (
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500 text-[10px]" data-testid="report-declined-badge">
                          <AlertTriangle className="w-3 h-3 mr-0.5" /> {reportOutcome === "rejected" ? "Rejected" : "Referred"}
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500 text-[10px]" data-testid="awaiting-fee-earner-review-badge">
                          Awaiting fee earner review
                        </Badge>
                      )}
                    </div>

                    {reportDeclined && (
                      <p className="text-[11px] text-amber-300" data-testid="report-declined-consequence">
                        AML is NOT satisfied. This matter is held at the AML check
                        until a replacement report has been reviewed and approved.
                        The admin team have been notified.
                      </p>
                    )}

                    {!reportApproved && (
                      <div className="flex flex-col gap-1.5 pt-1">
                        {/* BM-H3864: a file input sat here after BM-H415 removed
                            the upload, and choosing a file threw. The report is
                            filed in SharePoint, not attached here. */}
                        <label className="flex items-start gap-1.5 text-[11px] text-slate-300">
                          <input
                            type="checkbox"
                            checked={reviewAffirmed}
                            onChange={(e) => setReviewAffirmed(e.target.checked)}
                            data-testid="report-affirm-read-checkbox"
                            className="mt-0.5"
                          />
                          <span>
                            I have read the Thirdfort report for this matter and I am
                            satisfied with the identity evidence it contains.
                          </span>
                        </label>
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          data-testid="report-review-notes-input"
                          rows={2}
                          placeholder="Notes. Required if you do not approve."
                          className="text-[11px] bg-navy-deep border border-navy-border rounded px-2 py-1 text-slate-200"
                        />
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSubmitReportReview("referred")}
                            disabled={markingReportChecked}
                            className="text-xs"
                            data-testid="refer-report-btn"
                          >
                            Refer with queries
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSubmitReportReview("rejected")}
                            disabled={markingReportChecked}
                            className="text-xs border-amber-500 text-amber-300"
                            data-testid="reject-report-btn"
                          >
                            Reject report
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSubmitReportReview("approved")}
                            disabled={markingReportChecked || !reviewAffirmed}
                            className="bg-teal hover:bg-teal text-xs"
                            data-testid="mark-report-checked-btn"
                          >
                            {markingReportChecked ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                            {markingReportChecked ? "Recording..." : "Approve report"}
                          </Button>
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400">
                      The Thirdfort report PDF must be attached. It is stored to
                      SharePoint under AML/Thirdfort as the audit artefact, and the
                      approval is recorded against this matter as a risk assessment.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ---- Stage 4: FORF ---- */}
          <div className={`p-4 bg-[rgba(26,37,71,0.7)] rounded-md border ${canProceedToFORF ? 'border-blue-500/40 hover:border-blue-400' : 'border-navy-border'} transition-colors ${!canProceedToFORF ? 'opacity-60' : ''}`} data-testid="stage-4-forf">
            <div className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-md ${canProceedToFORF ? 'bg-blue-600' : 'bg-slate-400'} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>4</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-white">File Opening Request Form (FORF)</h4>
                  {!canProceedToFORF && (
                    <Badge className="bg-slate-100 text-slate-500 border-navy-border text-[10px]">
                      {!obDone ? "Waiting for the client's onboarding" : !thirdfortDone ? "Complete Thirdfort first" : "No matter reference yet"}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {canProceedToFORF
                    ? "All pre-requisites met. Open the FORF pre-populated with client data from OB."
                    : "Complete Stages 2 and 3 above before opening the FORF."
                  }
                </p>
                <Button
                  size="sm"
                  // The real FORF page, pre-filled from onboarding (24 Sep 2026).
                  onClick={() => navigate(forfPath(savedRef))}
                  disabled={!canProceedToFORF}
                  className="bg-blue-600 hover:bg-blue-700 text-xs disabled:bg-slate-300 disabled:text-slate-500"
                  data-testid="proceed-forf-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  Proceed to FORF & Risk Assessment
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">OR for existing matters</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Option B: File to SharePoint (Existing Matter) */}
          <div className="p-4 bg-[rgba(26,37,71,0.7)] rounded-md border border-teal hover:border-teal transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-md bg-teal text-white flex items-center justify-center text-xs font-bold flex-shrink-0">B</div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-teal mb-1">File to SharePoint (Existing Matter)</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Save the conflict check directly to an existing matter's Compliance folder.
                </p>
                {!filingResult ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={matterRef}
                        onChange={(e) => setMatterRef(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleFileToSharePoint()}
                        placeholder="e.g. SL.VEC.RES.00123"
                        className="flex-1 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid="file-sp-matter-ref-input"
                      />
                      <Button
                        onClick={handleFileToSharePoint}
                        disabled={filing || !matterRef.trim()}
                        size="sm"
                        className="bg-teal hover:bg-teal text-white text-xs"
                        data-testid="file-sp-btn"
                      >
                        {filing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4 mr-1" />}
                        {filing ? "Filing..." : "File"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-400 flex items-start gap-1">
                      <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span>Use the complete matter title, e.g. <strong>SL</strong> or <strong>BL</strong>.[FE initials].[dept code].[matter ref]</span>
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-teal" data-testid="file-sp-success">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Filed to <strong>{matterRef}</strong>/Compliance</span>
                    {filingResult.web_url && (
                      <a
                        href={filingResult.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal hover:text-teal ml-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5 inline" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Another Check */}
        <div className="mt-4 pt-4 border-t border-navy-border/60 flex justify-end">
          <Button
            onClick={onReset}
            variant="outline"
            className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white"
            data-testid="submit-another-check-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Submit Another Check
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// BM-H3474 (Vanessa, 3 Oct 2026): several company matters were checked under
// the director's name and the company itself was never searched.
const CompanyRegisteredNameNote = ({ testId }) => (
  <div
    className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-status-amber-dim/30 px-3 py-2 text-xs text-status-amber"
    data-testid={testId}
    role="note"
  >
    <span>
      If the prospective client is a company, please use its full registered name, as shown at
      Companies House, not the name of a director or contact.
    </span>
  </div>
);

export default function ConflictsSearch() {
  const navigate = useNavigate();
  const { authStatus } = useBonsai();
  const signedInUser = authStatus?.user_email || authStatus?.user_name || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState(null);
  const [results, setResults] = useState(null);
  // BM-H265: the filtered-match panel starts collapsed but is always present.
  const [showFilteredMatches, setShowFilteredMatches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [indexStatus, setIndexStatus] = useState(null);
  
  // Record clearance state
  const [showClearanceForm, setShowClearanceForm] = useState(false);
  const [clearanceType, setClearanceType] = useState("clear"); // clear | clear_with_conditions | na | not_ready
  const [decisionMaker, setDecisionMaker] = useState("");
  const [feeEarner, setFeeEarner] = useState("");
  const [practiceArea, setPracticeArea] = useState("");
  const [clientType, setClientType] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");
  // Brief description / matter title for the auto-generated
  // SharePoint folder (14 May 2026, Liezel's ask). Required since 29 Sep 2026. Free
  // text. Persisted on the conflict_check record via the new
  // brief_description field on ConflictCheckCreate.
  const [briefDescription, setBriefDescription] = useState("");
  // BM-H24: matterReference state removed — the input that fed it was
  // deleted (references are minted server-side only). Kept out of the POST
  // to end the 422-misread-as-failure retry/duplicate loop.
  const [notReadyReason, setNotReadyReason] = useState("");
  const [savingClearance, setSavingClearance] = useState(false);
  const [savedConflictCheck, setSavedConflictCheck] = useState(null);
  // Issue #2959: matches the user confirms as the client's own record, each
  // with a reason ({ [matchId]: { checked, reason } }), and whether the
  // signed-in user is a director who may approve a genuine conflict inline.
  const [ownClientConfirmations, setOwnClientConfirmations] = useState({});
  const [canApproveConflicts, setCanApproveConflicts] = useState(false);
  const [directorReason, setDirectorReason] = useState("");
  // BM-H4514: "no_conflict" | "with_conditions", chosen by the director.
  const [directorOutcome, setDirectorOutcome] = useState("");
  // Issue #2959: which route a potential-conflict save takes.
  //   every match confirmed as the client's own -> clears, no approval;
  //   otherwise a director approves inline with a reason;
  //   otherwise the emailed supervisor approval, as before.
  const potentialConflictPath = Boolean(results?.has_potential_conflict) && clearanceType === "clear_with_conditions";
  const allOwnClientConfirmed = potentialConflictPath && everyMatchConfirmedOwnClient(results?.results, ownClientConfirmations);
  const directorApprovalPath = potentialConflictPath && !allOwnClientConfirmed && canApproveConflicts;
  const clearanceFormRef = useRef(null);
  // BM-H13 (2 Jul 2026, Vanessa): stable idempotency key per submit attempt.
  // Set once when the user first opens the clearance form; sent on every
  // POST /conflict-checks retry so a network-glitch retry does not mint a
  // second matter number on the backend. Reset only after a genuine success
  // (or when the form is dismissed).
  const clientRequestIdRef = useRef(null);
  const [matchReviewed, setMatchReviewed] = useState(false);
  
  // Client name - required before clearance
  const [clientName, setClientName] = useState("");

  // BM-H77 Phase-2 — quote → conflict check prefill.
  // QuoteView's "Start conflict check" button navigates here with six
  // query params so the FE doesn't retype what they already put in the
  // quote. source_quote_ref + estimated_fee are stashed on the saved
  // conflict-check record so the downstream matter can be linked back to
  // the quote for accepted-quote reconciliation.
  const [sourceQuoteRef, setSourceQuoteRef] = useState("");
  const [estimatedFee, setEstimatedFee] = useState("");
  // BM-H615: the quote's client email and matter description. The prefill
  // effect used to call NextStepsPanel's own setters, which do not exist in
  // this component, so a quote link with either param threw a ReferenceError
  // and took the page down. They are held here and passed to NextStepsPanel,
  // whose onboarding form asks for both.
  const [quoteClientEmail, setQuoteClientEmail] = useState("");
  const [quoteMatterDescription, setQuoteMatterDescription] = useState("");
  
  // Other Side / Counterparty names
  const [otherSideNames, setOtherSideNames] = useState("");

  // Stage A1: per-other-side party_type selector. Keyed by index of the
  // ;-split list. Default empty so the user must explicitly choose
  // (the backend will 422 on empty).
  const [otherSideTypes, setOtherSideTypes] = useState({});  // { 0: "individual", 1: "company" }

  // Joint clients - identified at the outset
  const [jointClients, setJointClients] = useState([]); // [{ name: "", email: "" }]

  // BM-H397: every client on a joint matter has to be in the conflict
  // search before the matter can be cleared. The server refuses otherwise,
  // so the screen says who is missing while it can still be fixed.
  const uncoveredJoint = useMemo(
    () => uncoveredJointClients(jointClients, searchQuery, clientName),
    [jointClients, searchQuery, clientName]
  );

  // BM-H3791 (6 Oct 2026, Vanessa): "i can't press submit". The save button
  // was grey with every field near it filled in; a match further up was
  // ticked "This match is my client" with no reason. Each condition that
  // disables the save is listed here with what to do, and the button is
  // disabled from this list, so the two cannot drift apart.
  const saveBlockers = [
    !feeEarner && "Pick a fee earner at the top of the page.",
    !practiceArea && "Pick a practice area at the top of the page.",
    !clientName.trim() && "Enter the client name at the top of the page.",
    !clientType && "Choose whether the client is an individual, company or LLP.",
    !decisionMaker && "Choose the decision maker.",
    clearanceType === "not_ready" && !notReadyReason.trim() && "Give the reason for deferring.",
    clearanceType !== "not_ready" && uncoveredJoint.length > 0 && "Every joint client has to be searched first (see the warning above).",
    !ownClientConfirmationsValid(ownClientConfirmations) && `A match ticked "This match is my client" needs a reason of at least ${OWN_CLIENT_MIN_REASON} characters, or untick it.`,
    directorApprovalPath && !directorApprovalStatus(directorOutcome) && "Choose No conflict or With conditions.",
    directorApprovalPath && directorReason.trim().length < DIRECTOR_MIN_REASON && (directorOutcome === "with_conditions" ? `State the conditions (at least ${DIRECTOR_MIN_REASON} characters).` : `Give your reason for clearing this conflict (at least ${DIRECTOR_MIN_REASON} characters).`),
  ].filter(Boolean);

  // Explicit search — only fires when user presses Enter or clicks Search
  const [activeQuery, setActiveQuery] = useState("");

  // BM-H77 Phase-2 — apply quote prefill on mount (one-shot).
  // Runs once so a FE editing the client name mid-flow doesn't get
  // reverted every render. Guarded so an empty ?param on a normal visit
  // doesn't clobber anything.
  const [searchParams] = useSearchParams();
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const p = {
      source_quote_ref: searchParams.get("source_quote_ref") || "",
      client_name: searchParams.get("client_name") || "",
      client_email: searchParams.get("client_email") || "",
      matter_description: searchParams.get("matter_description") || "",
      fee_earner_initials: searchParams.get("fee_earner_initials") || "",
      estimated_fee: searchParams.get("estimated_fee") || "",
    };
    if (!p.source_quote_ref && !p.client_name && !p.client_email && !p.matter_description) {
      return;  // no quote prefill on this visit
    }
    prefillAppliedRef.current = true;
    if (p.client_name) setClientName(p.client_name);
    if (p.client_email) setQuoteClientEmail(p.client_email);
    if (p.matter_description) setQuoteMatterDescription(p.matter_description);
    if (p.fee_earner_initials) setFeeEarner(p.fee_earner_initials);
    if (p.source_quote_ref) setSourceQuoteRef(p.source_quote_ref);
    if (p.estimated_fee) setEstimatedFee(p.estimated_fee);
  }, [searchParams]);

  // BM-H613 (25 Sep 2026): /conflict-search?complete_deferred=<id> completes a
  // check that was deferred at the first step. The client, fee earner,
  // practice area and joint clients come from the deferred record; the
  // search of the now-known other side is run here as for any check, and the
  // save goes onto the same record and matter reference.
  const [completingDeferred, setCompletingDeferred] = useState(null);
  useEffect(() => {
    const deferredId = searchParams.get("complete_deferred");
    if (!deferredId) return undefined;
    let cancelled = false;
    axios.get(`${API}/conflict-checks/${encodeURIComponent(deferredId)}`)
      .then((res) => {
        if (cancelled) return;
        const cc = res.data || {};
        if (cc.conflict_status !== "not_ready") {
          toast.error("This conflict check is not deferred, so there is nothing to complete.");
          return;
        }
        setCompletingDeferred(cc);
        setClientName(cc.client_name || "");
        setClientType(cc.client_type || "");
        setFeeEarner(cc.fee_earner || "");
        setPracticeArea(cc.matter_type || "");
        setJointClients((cc.joint_clients || []).map((j) => ({ name: j.name || "", email: j.email || "" })));
        // BM-H3772 (6 Oct 2026, Vanessa): the matter was opened and staged at
        // deferral, so its title and decision maker come from that record and
        // the clearance form does not ask for them again.
        setBriefDescription(cc.brief_description || "");
        if (cc.decision_maker_name) setDecisionMaker(cc.decision_maker_name);
        const others = (cc.other_side_parties || []).filter((p) => (p.legal_name || "").trim());
        if (others.length) {
          setOtherSideNames(others.map((p) => p.legal_name.trim()).join("; "));
          setOtherSideTypes(Object.fromEntries(others.map((p, i) => [i, p.party_type || ""])));
        }
      })
      .catch((err) => {
        if (!cancelled) toast.error(errorMessage(err, "Could not load the deferred conflict check"));
      });
    return () => { cancelled = true; };
  }, [searchParams]);

  // Issue #2959: ask the server whether the signed-in user may approve a
  // conflict clearance (the directors). Same predicate the /self-approve
  // endpoint enforces; a failure leaves the emailed approval flow in place.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/conflict-checks/me/can-approve`)
      .then((res) => { if (!cancelled) setCanApproveConflicts(Boolean(res.data?.can_approve)); })
      .catch(() => { if (!cancelled) setCanApproveConflicts(false); });
    return () => { cancelled = true; };
  }, []);

  // A new search is a new set of matches: earlier confirmations do not carry over.
  useEffect(() => {
    setOwnClientConfirmations({});
    setDirectorReason("");
    setDirectorOutcome("");
  }, [results]);

  // Check index status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/sharepoint/status`);
        setIndexStatus(response.data);
      } catch (err) {
        console.error("Failed to check SharePoint status:", err);
      }
    };
    checkStatus();
  }, []);

  // Search only when explicitly triggered (Enter key or Search button)
  const performSearch = useCallback(async (queryOverride) => {
    const q = queryOverride || activeQuery;
    if (q.length < 2) {
      setResults(null);
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null); // Clear stale results immediately
    setMatchReviewed(false); // Reset review checkbox for new search

    try {
      const params = { query: q };
      if (entityTypeFilter) {
        params.entity_type = entityTypeFilter;
      }

      // Build party_labels map from client names and other side names
      const labels = {};
      const clientParts = clientName.split(";").map(n => n.trim().toLowerCase()).filter(n => n.length >= 2);
      const otherParts = otherSideNames.split(";").map(n => n.trim().toLowerCase()).filter(n => n.length >= 2);
      // Include joint client names in the client party labels
      const jointParts = jointClients.map(jc => jc.name.trim().toLowerCase()).filter(n => n.length >= 2);
      clientParts.forEach(n => { labels[n] = "client"; });
      jointParts.forEach(n => { labels[n] = "client"; });
      otherParts.forEach(n => { labels[n] = "other_side"; });
      if (Object.keys(labels).length > 0) {
        params.party_labels = JSON.stringify(labels);
      }

      const response = await axios.get(`${API}/conflicts/search`, { 
        params,
        timeout: 60000  // 60 second timeout for large lists
      });
      
      if (response.data.error) {
        setError(response.data.error);
        setResults(null);
      } else {
        setResults(response.data);
      }
    } catch (err) {
      const backendError = err.response?.data?.error || err.response?.data?.detail;
      if (backendError) {
        setError(backendError);
      } else if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        setError("Search timed out — SharePoint may be slow. Please try again.");
      } else if (!err.response) {
        setError("Network error — cannot reach the server. Please check your connection.");
      } else {
        setError(`Search failed (${err.response?.status || "unknown"}). Please try again or contact support.`);
      }
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [activeQuery, entityTypeFilter, clientName, otherSideNames]);

  const triggerSearch = useCallback(() => {
    // Auto-build search query from client + joint clients + other side names if search field is empty
    let effectiveQuery = searchQuery.trim();
    if (!effectiveQuery) {
      const parts = [];
      if (clientName.trim()) parts.push(clientName.trim());
      // Include joint client names in the search
      jointClients.forEach(jc => { if (jc.name.trim()) parts.push(jc.name.trim()); });
      if (otherSideNames.trim()) parts.push(otherSideNames.trim());
      effectiveQuery = parts.join("; ");
      if (effectiveQuery) {
        setSearchQuery(effectiveQuery);
      }
    } else {
      // BM-H397: a typed query, or a joint client added after the last
      // search, used to leave that person unsearched. Fold any missing
      // joint client into the query rather than quietly leaving them out.
      const missing = uncoveredJointClients(jointClients, effectiveQuery, clientName);
      if (missing.length > 0) {
        effectiveQuery = [effectiveQuery, ...missing].join("; ");
        setSearchQuery(effectiveQuery);
      }
    }
    if (effectiveQuery.length < 2) {
      setError("Please enter at least 2 characters to search.");
      return;
    }
    setActiveQuery(effectiveQuery);
    performSearch(effectiveQuery);
  }, [searchQuery, performSearch, clientName, otherSideNames, jointClients]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      triggerSearch();
    }
  }, [triggerSearch]);

  const getEntityIcon = (entityType) => {
    return entityType === "Client" ? (
      <User className="w-4 h-4 text-teal" />
    ) : (
      <Users className="w-4 h-4 text-status-amber" />
    );
  };

  const handleSaveClearance = async () => {
    if (!clientName.trim()) {
      toast.error("Please enter the client name before recording a clearance");
      return;
    }
    if (!decisionMaker) {
      toast.error("Please select a decision maker");
      return;
    }
    if (!feeEarner) {
      toast.error("Please select a fee earner");
      return;
    }
    if (!practiceArea) {
      toast.error("Please select a practice area");
      return;
    }
    if (!clientType) {
      toast.error("Please select the client type (individual / company / LLP)");
      return;
    }
    // SP folder brief description (29 Sep 2026, Liezel's report; Vanessa
    // chose "Required"): the SharePoint folder name's third slot. Left blank
    // it fell back to the search text, every party's name strung together.
    if (!briefDescription.trim()) {
      toast.error("Please enter a brief description / matter title. It names the SharePoint folder.");
      return;
    }
    if (clearanceType === "not_ready" && !notReadyReason.trim()) {
      toast.error("Please provide a reason for deferring the conflict check");
      return;
    }
    // BM-H397: a clearance covers every client on the matter, so every
    // client has to have been searched. Deferral is not a clearance.
    if (clearanceType !== "not_ready" && uncoveredJoint.length > 0) {
      toast.error(uncoveredMessage(uncoveredJoint));
      return;
    }
    setSavingClearance(true);
    // BM-H13: mint a stable idempotency key on the first attempt of this
    // submission. Retries reuse the same key so the server can dedupe.
    if (!clientRequestIdRef.current) {
      clientRequestIdRef.current =
        (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
          ? crypto.randomUUID()
          : `cc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    try {
      const statusMap = {
        clear: results?.has_potential_conflict ? "clear_with_conditions" : "clear",
        clear_with_conditions: "clear_with_conditions",
        na: "na",
        not_ready: "not_ready"
      };
      const notesMap = {
        clear: decisionNotes || `No conflicts found for "${clientName.trim()}"`,
        clear_with_conditions: decisionNotes || `Cleared with conditions for "${clientName.trim()}"`,
        na: decisionNotes || `N/A — Advice only, no other side to check for "${clientName.trim()}"`,
        not_ready: decisionNotes || `Deferred: ${notReadyReason}`
      };
      const hitsMap = {
        clear: `Search: "${searchQuery}" — ${results?.total_matches || 0} matches, all clear`,
        clear_with_conditions: `Search: "${searchQuery}" — ${results?.total_matches || 0} matches, cleared with conditions`,
        na: `N/A — Advice only matter for "${clientName.trim()}", no conflict search required`,
        not_ready: `Deferred for "${clientName.trim()}" — ${notReadyReason}`
      };

      // If potential conflicts found and clearing with conditions, submit for supervisor approval
      const needsApproval = results?.has_potential_conflict && clearanceType === "clear_with_conditions";

      // First, create the conflict check record
      const otherSideList = otherSideNames.split(";")
        .map(n => n.trim())
        .filter(n => n.length > 0);
      // Stage A1: each other-side party gets its own party_type (no
      // hardcoded "individual"). The backend 422s on empty.
      const missingType = otherSideList.findIndex((_, i) => !otherSideTypes[i]);
      if (otherSideList.length > 0 && missingType !== -1) {
        toast.error(`Please choose a party type for "${otherSideList[missingType]}".`);
        setSavingClearance(false);
        return;
      }
      // BM-H4487: the create and the director's approval are sent as one
      // attempt, so a lost answer on either is confirmed by sending the
      // same attempt again (same key; both steps replay a finished save).
      const attemptSave = async () => {
        const res = await axios.post(`${API}/conflict-checks`, {
          client_name: clientName.trim(),
          client_type: clientType,
          other_side_parties: otherSideList.map((n, idx) => ({
            party_type: otherSideTypes[idx] || "individual",
            legal_name: n,
          })),
          // Stage A1: persist joint clients on the conflict check itself
          // so the OB stub + downstream FORF prefill see them immediately.
          joint_clients: jointClients
            .filter(jc => jc.name && jc.name.trim())
            .map(jc => ({ name: jc.name.trim(), email: (jc.email || "").trim() })),
          conflict_status: needsApproval ? "pending_approval" : (statusMap[clearanceType] || clearanceType),
          decision_maker_name: decisionMaker,
          decision_notes: notesMap[clearanceType],
          // 14 May 2026 — explicit matter title for the SharePoint folder
          // name. Required on this form since 29 Sep 2026; the backend
          // fallback to search_query, then client_name, remains only for
          // older clients and other entry points.
          brief_description: briefDescription.trim(),
          search_query: searchQuery || "",
          hits_summary: results || {},
          // BM-H24: matter_reference is NOT sent. References are minted
          // server-side only (SharePoint poll). Sending a client value got
          // a 422 that fee earners misread as a failed save — the source of
          // the retry-and-duplicate loop.
          fee_earner: feeEarner,
          practice_area: practiceArea,
          not_ready_reason: clearanceType === "not_ready" ? notReadyReason : null,
          submitted_by: signedInUser || null,
          // BM-H77 Phase-2 — quote linkage. Persisted on the conflict-check
          // record so the accepted quote can be reconciled with the opened
          // matter downstream. Backend keeps extras verbatim in the audit
          // trail; omitting them (normal visits) is safe.
          source_quote_ref: sourceQuoteRef || null,
          estimated_fee: estimatedFee || null,
          // BM-H13: idempotency key. Backend dedupes on this so a network
          // retry does not create a second matter folder on SharePoint.
          client_request_id: clientRequestIdRef.current,
          // Issue #2959: matches confirmed as the client's own record, each with
          // a reason. The server records who confirmed from the session and
          // clears the check when every match is the client's own.
          own_client_confirmations: ownClientConfirmationPayload(ownClientConfirmations),
          // BM-H613: completes the deferred record instead of opening a new matter.
          completes_deferred_id: completingDeferred?.id || null,
        });

        // Issue #2959: the server may already have cleared the check (every
        // match was the client's own). Only a check still pending goes on to
        // director approval.
        const stillPending = needsApproval && res.data?.conflict_status === "pending_approval";
        let approveRes = null;
        if (stillPending && canApproveConflicts) {
          // Issue #2959: a director approves a genuine conflict here, with a
          // reason, instead of being sent round the emailed approval loop.
          approveRes = await axios.post(
            `${API}/conflict-checks/${res.data.id}/self-approve`,
            {
              completing_person_id: signedInUser,
              approved: true,
              decision_notes: directorReason.trim(),
              // BM-H4514: the director's own choice, never a fixed status.
              new_status: directorApprovalStatus(directorOutcome),
            },
          );
        }
        return { res, approveRes, stillPending };
      };
      const { value: saved, retried } = await confirmSave(attemptSave, {
        onWaiting: () => {
          toast.loading(
            "Still saving. The server is taking a while to answer, so CaseMinder " +
            "is checking for you. Please do not press Save again.",
            { id: SAVE_CONFIRM_TOAST_ID },
          );
        },
      });
      toast.dismiss(SAVE_CONFIRM_TOAST_ID);
      const { res, approveRes, stillPending } = saved;

      if (approveRes) {
        const ref = approveRes.data?.matter_reference;
        toast.success(
          retried || approveRes.data?.already_approved
            ? `Saved. Conflict approved by you and cleared${ref ? `: matter ${ref}` : ""}.`
            : "Conflict approved by you and cleared.",
        );
        setSavedConflictCheck({
          ...res.data,
          ...(approveRes.data || {}),
          conflict_status: approveRes.data?.new_status || approveRes.data?.conflict_status || "clear_with_conditions",
          joint_clients: jointClients.filter(jc => jc.name.trim()),
        });
      } else if (stillPending) {
        // Submit for supervisor approval
        try {
          const approvalRes = await axios.post(`${API}/conflict-approval/submit`, {
            conflict_check_id: res.data.id,
            decision_notes: notesMap[clearanceType],
          });
          toast.success(`Submitted for approval to ${approvalRes.data.supervisor_name}. They will receive an email with all match details.`);
          setSavedConflictCheck({
            ...res.data,
            conflict_status: "pending_approval",
            approval_supervisor: approvalRes.data.supervisor_name,
            joint_clients: jointClients.filter(jc => jc.name.trim()),
          });
        } catch (approvalErr) {
          // Approval submission failed — still saved as pending
          console.error("Approval submission error:", approvalErr);
          toast.warning(errorMessage(approvalErr, "Conflict check saved but approval email may not have been sent. Contact your supervisor directly."));
          setSavedConflictCheck({ ...res.data, conflict_status: "pending_approval", joint_clients: jointClients.filter(jc => jc.name.trim()) });
        }
      } else {
        const statusLabel = res.data?.cleared_via === "own_client_confirmed"
          ? "Cleared: every match confirmed as your client's own record"
          : clearanceType === "na" ? "N/A recorded" : clearanceType === "not_ready" ? "Deferred — recorded" : "Conflict check saved";
        // BM-H24: if the backend replayed an existing record (BM-H13
        // idempotent replay), this matter was already saved on a prior
        // attempt — say so plainly rather than implying a fresh save.
        if (res.data?.completed_deferred) {
          toast.success(res.data.message || "Deferred conflict check completed");
        } else if (res.data?.idempotent_replay && retried) {
          // BM-H4487: our own confirmation of a save whose answer was lost.
          toast.success(`Saved${res.data?.matter_reference ? `: matter ${res.data.matter_reference}` : ""}.`);
        } else if (res.data?.idempotent_replay) {
          toast.success("This matter already had a conflict check — reusing it, no duplicate created.");
        } else {
          toast.success(`${statusLabel} and staged`);
        }
        setSavedConflictCheck({ ...res.data, conflict_status: res.data?.conflict_status || statusMap[clearanceType] || clearanceType, joint_clients: (res.data?.joint_clients && res.data.joint_clients.length ? res.data.joint_clients : jointClients.filter(jc => jc.name.trim())) });
      }
      setShowClearanceForm(false);
      // BM-H13: successful save — clear the idempotency key so the next
      // fresh submit gets its own key. If the user reopens the form later
      // for a new matter, a new key will be minted on next submit.
      clientRequestIdRef.current = null;
    } catch (e) {
      // 14May2026: no swallowed exceptions. Surface backend HTTPException
      // detail verbatim via window.errorMessage(err, fallback). The previous
      // generic toast hid the real cause (502 from upstream CCL/matter-ref
      // failures, party_type 422s, conflict_status validation, etc.).
      // BM-H13 (2 Jul 2026): on a no-response network error we cannot know
      // whether the server actually succeeded. The client_request_id makes
      // retries safe (backend dedupes), so tell the user exactly that
      // instead of the generic "check your connection" toast that led to
      // duplicate matter folders (.1197 / .1199 / .1201).
      console.error("Failed to save clearance:", e);
      // BM-H24 (3 Jul 2026, Vanessa) — the duplicate-trap 409 is NOT a
      // failure: it means this matter ALREADY has a cleared conflict
      // check, so nothing needs saving. Surfacing it as a red error made
      // fee earners think the save had failed and click Save again —
      // which then hit the trap again (a loop) or, worse, minted a
      // duplicate matter folder when the idempotency key had been
      // cleared. Treat it as an already-saved success: advance straight
      // to Next Steps using the existing record the backend echoes back.
      const _detail = e?.response?.data?.detail;
      const _isDuplicateTrap =
        e?.response?.status === 409 &&
        _detail && typeof _detail === "object" &&
        _detail.code === "duplicate_cleared_conflict_check";
      if (_isDuplicateTrap) {
        const existing = _detail.existing_record || {
          id: _detail.existing_conflict_id,
          matter_reference: _detail.existing_record?.matter_reference || null,
          conflict_status: _detail.existing_status || "clear",
        };
        toast.success("This matter already has a cleared conflict check — nothing to duplicate. Continuing.");
        setSavedConflictCheck({
          ...existing,
          conflict_status: existing.conflict_status || "clear",
          joint_clients: (existing.joint_clients && existing.joint_clients.length)
            ? existing.joint_clients
            : jointClients.filter(jc => jc.name.trim()),
        });
        setShowClearanceForm(false);
        // Resolved — a fresh submit later should mint its own key.
        clientRequestIdRef.current = null;
      } else if (e?.response?.status === 409 && _detail && typeof _detail === "object" && _detail.code === "conflict_check_save_unfinished") {
        // BM-H3722 (5 Oct 2026): the first press of this save is still
        // running or was cut off after it created the matter. The server
        // refused to create a second one; say so plainly, in amber.
        toast.warning(_detail.message, { duration: 15000 });
      } else if (e && (!e.response || (e.response.status >= 500 && !(e.response.data && typeof e.response.data === "object" && e.response.data.detail)))) {
        // BM-H3722: Azure answers a request it gave up on (or a restart
        // mid-request) with a bare "Backend call failure" page, not our JSON.
        // The save may well have gone through, exactly like a dropped
        // connection. The key is kept, and the server now refuses to create
        // a second matter on the next press.
        // BM-H4260 (13 Oct 2026): Liezel's N/A save for BL.VEC.008.1562 saved
        // in full and still showed a RED "Backend call failure", because the
        // test above only knew 502 to 504 with a text body. Any 5xx that is
        // not our own API's answer (ours always carries `detail`) is the
        // platform's, and says nothing about whether the save happened.
        // BM-H4487: reached only after CaseMinder has asked the server again
        // for about three minutes (confirmSave) and still had no answer.
        toast.warning(
          "The server has still not confirmed the save after three minutes. " +
          "Your matter may already have been created. Check the Pipeline " +
          "Tracker before saving again. Saving again will not create a " +
          "second matter.",
          { duration: 20000 }
        );
      } else {
        toast.error(_errorMessage(e, "Failed to save conflict check"));
      }
      // Do NOT clear clientRequestIdRef.current here (except the resolved
      // duplicate-trap path above) — retain it so the next click sends the
      // same key and the backend dedupes.
    } finally {
      toast.dismiss(SAVE_CONFIRM_TOAST_ID);
      setSavingClearance(false);
    }
  };


  const getClientTypeIcon = (clientType) => {
    if (clientType === "Company") {
      return <Building2 className="w-3 h-3" />;
    }
    return <User className="w-3 h-3" />;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-txt-muted rounded-md"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-200/60" />
              <div>
                <h1 className="text-lg font-semibold text-teal">Conflicts Search</h1>
                <p className="text-xs text-slate-500">Search clients and counterparties before opening a new matter</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TestModeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 animate-fade-in-up">
        <TestModeBanner />
        {completingDeferred && (
          <div className="mb-4 rounded-md border border-amber-500/50 bg-status-amber-dim/30 px-4 py-3 text-sm text-white"
               data-testid="completing-deferred-banner">
            <strong>Completing the deferred conflict check on{" "}
              {completingDeferred.matter_reference || completingDeferred.provisional_id}</strong>
            {" "}for {completingDeferred.client_name}. Deferred because:{" "}
            {completingDeferred.not_ready_reason || "not recorded"}. Search the other side
            now known, then record the clearance. It is saved on this matter; no new
            reference or folder is created.
          </div>
        )}
        {/* BM-H613: deferred checks by age, so they get chased. */}
        {!completingDeferred && !savedConflictCheck && (
          <DeferredConflictChecks
            onComplete={(id) => navigate(`/conflict-search?complete_deferred=${encodeURIComponent(id)}`)}
          />
        )}
        {/* BM-H615: say what came from the quote and where it goes, so the
            email and description are not held out of sight. */}
        {sourceQuoteRef && (quoteClientEmail || quoteMatterDescription) && !savedConflictCheck && (
          <div className="mb-4 rounded-md border border-teal/30 bg-teal-dim/10 px-4 py-3 text-sm text-txt-muted"
               data-testid="quote-prefill-note">
            From quote <strong className="text-white">{sourceQuoteRef}</strong>.
            {quoteClientEmail && <> Client email: <span className="text-white">{quoteClientEmail}</span>.</>}
            {quoteMatterDescription && <> Matter description: <span className="text-white">{quoteMatterDescription}</span>.</>}
            {" "}These are filled into the onboarding form once the conflict check is saved.
          </div>
        )}
        {/* Search Section */}
        <div className="glass-card rounded-md mb-6">
          <div className="p-5 pb-4 border-b border-navy-border/60">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white">
              <Search className="w-5 h-5 text-teal" />
              Search Conflicts Index
            </h2>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              {/* 14May2026: Fee Earner + Practice Area collected BEFORE search.
                  These two fields are the spine of the matter reference
                  (BL.<FE>.<PA>.NNNN) and must appear on the audit-log PDF.
                  Previously hidden inside the clearance form which only
                  rendered after the search ran — wrong sequence. */}
              <div className="space-y-2 p-3 bg-teal-dim/10 rounded-md border border-teal/20" data-testid="matter-spine-block">
                <p className="text-[10px] font-medium text-teal uppercase tracking-wider">
                  Matter spine — required before search
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-white">Fee Earner <span className="text-status-red">*</span></Label>
                    <Select value={feeEarner} onValueChange={setFeeEarner}>
                      <SelectTrigger data-testid="upfront-fee-earner" className="h-11 border-navy-border focus:border-teal focus:ring-teal mt-1">
                        <SelectValue placeholder="Select fee earner" />
                      </SelectTrigger>
                      <SelectContent>
                        {FEE_EARNERS.map((fe) => (
                          <SelectItem key={fe.name} value={fe.name}>{fe.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-white">Practice Area <span className="text-status-red">*</span></Label>
                    <Select value={practiceArea} onValueChange={setPracticeArea}>
                      <SelectTrigger data-testid="upfront-practice-area" className="h-11 border-navy-border focus:border-teal focus:ring-teal mt-1">
                        <SelectValue placeholder="Select practice area" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRACTICE_AREAS.map((pa) => (
                          <SelectItem key={pa.value} value={pa.value}>{pa.value} — {pa.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Fee Earner and Practice Area generate the matter reference and appear on the audit-log PDF. Both are required before running the conflict search.</p>
              </div>

              {/* Client Name Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-white">Client Name <span className="text-status-red">*</span></Label>
                <CompanyRegisteredNameNote testId="company-registered-name-note" />
                <Input
                  type="text"
                  placeholder="Enter client's full name (separate multiple names with ;)"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="h-11 text-base border-navy-border focus:border-teal focus:ring-teal"
                  data-testid="client-name-input"
                />
                <p className="text-xs text-slate-500">Separate multiple client names with a semicolon (;). This name will be used for the matter folder and onboarding record.</p>
              </div>

              {/* Joint Clients Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-white flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-blue-400" />
                    Joint Clients
                  </Label>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => setJointClients(prev => [...prev, { name: "", email: "" }])}
                    className="text-xs border-blue-500/40 text-blue-400 hover:bg-blue-900/20 h-7"
                    data-testid="add-joint-client-btn"
                  >
                    + Add Joint Client
                  </Button>
                </div>
                {jointClients.length === 0 && (
                  <p className="text-xs text-slate-500">No joint clients. Click "Add Joint Client" if this is a joint matter (e.g. joint property purchase).</p>
                )}
                {jointClients.map((jc, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-navy-hover/60 rounded-md border border-blue-500/20">
                    <span className="text-xs text-blue-400 font-bold w-5 flex-shrink-0">{idx + 1}.</span>
                    <Input
                      type="text"
                      placeholder="Joint client full name"
                      value={jc.name}
                      onChange={(e) => {
                        const updated = [...jointClients];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setJointClients(updated);
                      }}
                      className="h-9 text-sm border-navy-border flex-1"
                      data-testid={`joint-client-name-${idx}`}
                    />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={jc.email}
                      onChange={(e) => {
                        const updated = [...jointClients];
                        updated[idx] = { ...updated[idx], email: e.target.value };
                        setJointClients(updated);
                      }}
                      className="h-9 text-sm border-navy-border flex-1"
                      data-testid={`joint-client-email-${idx}`}
                    />
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => setJointClients(prev => prev.filter((_, i) => i !== idx))}
                      className="h-7 w-7 p-0 text-status-red hover:text-status-red hover:bg-status-red-dim/20 flex-shrink-0"
                      data-testid={`remove-joint-client-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Other Side / Counterparty Names Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-white">Other Side / Counterparty</Label>
                <Input
                  type="text"
                  placeholder="Enter other side name(s) (separate multiple with ;)"
                  value={otherSideNames}
                  onChange={(e) => setOtherSideNames(e.target.value)}
                  className="h-11 text-base border-navy-border focus:border-amber-600/60 focus:ring-amber-600/60"
                  data-testid="other-side-names-input"
                />
                <p className="text-xs text-slate-500">Enter counterparty/other side names to check for conflicts. Results will be labelled separately from client searches.</p>
                {/* Stage A1: per-other-side party_type selector. Backend
                    422s on missing party_type. */}
                {otherSideNames.split(";").map(n => n.trim()).filter(n => n.length > 0).length > 0 && (
                  <div className="space-y-1.5 mt-2 p-2 bg-status-amber-dim/10 rounded-md border border-amber-500/20">
                    <p className="text-[10px] font-medium text-status-amber uppercase tracking-wider">
                      Other-side party type — required for each row
                    </p>
                    {otherSideNames.split(";").map(n => n.trim()).filter(n => n.length > 0).map((name, idx) => (
                      <div key={`os-type-${idx}`} className="flex items-center gap-2">
                        <span className="text-xs text-slate-300 w-40 truncate" title={name}>{name}</span>
                        <select
                          value={otherSideTypes[idx] || ""}
                          onChange={(e) => setOtherSideTypes(prev => ({ ...prev, [idx]: e.target.value }))}
                          className="flex-1 px-2 py-1 text-xs border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white"
                          data-testid={`other-side-party-type-${idx}`}
                        >
                          <option value="">— Select —</option>
                          <option value="individual">Individual</option>
                          <option value="company">Company</option>
                          <option value="llp">LLP</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Parties Summary */}
              {(clientName.trim() || otherSideNames.trim() || jointClients.some(jc => jc.name.trim())) && (
                <div className="flex flex-wrap items-center gap-2 p-3 bg-navy-hover/60 rounded-md">
                  <span className="text-xs text-slate-400 mr-1">Will search:</span>
                  {clientName.split(";").filter(n => n.trim().length >= 2).map((name, i) => (
                    <span key={`c-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-dim/40 text-teal border border-teal/40" data-testid={`party-tag-client-${i}`}>
                      <User className="w-3 h-3" />
                      {name.trim()}
                      <span className="text-[10px] text-teal/70 ml-0.5">Client</span>
                    </span>
                  ))}
                  {jointClients.filter(jc => jc.name.trim().length >= 2).map((jc, i) => (
                    <span key={`j-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-900/40 text-blue-300 border border-blue-600/40" data-testid={`party-tag-joint-${i}`}>
                      <Users className="w-3 h-3" />
                      {jc.name.trim()}
                      <span className="text-[10px] text-blue-400/70 ml-0.5">Joint Client</span>
                    </span>
                  ))}
                  {otherSideNames.split(";").filter(n => n.trim().length >= 2).map((name, i) => (
                    <span key={`o-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-status-amber-dim/40 text-status-amber border border-amber-600/40" data-testid={`party-tag-other-${i}`}>
                      <Users className="w-3 h-3" />
                      {name.trim()}
                      <span className="text-[10px] text-status-amber/70 ml-0.5">Other Side</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Search Input */}
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Auto-populated from names above, or type custom search terms"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-10 h-12 text-base border-navy-border focus:border-teal focus:ring-teal"
                    data-testid="conflicts-search-input"
                  />
                  {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
                  )}
                </div>
                <Button
                  onClick={triggerSearch}
                  disabled={
                    loading ||
                    !feeEarner ||
                    !practiceArea ||
                    (!searchQuery.trim() && !clientName.trim() && !otherSideNames.trim())
                  }
                  title={
                    !feeEarner || !practiceArea
                      ? "Pick Fee Earner and Practice Area first — they generate the matter reference."
                      : undefined
                  }
                  className="h-12 px-6 bg-teal hover:bg-teal-hover text-white font-medium"
                  data-testid="conflicts-search-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  {loading ? "Searching..." : "Search"}
                </Button>
              </div>

              {/* Filter Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Filter:</span>
                <Button
                  variant={entityTypeFilter === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEntityTypeFilter(null)}
                  className={entityTypeFilter === null ? "bg-teal hover:bg-teal-hover" : ""}
                  data-testid="filter-all"
                >
                  All
                </Button>
                <Button
                  variant={entityTypeFilter === "Client" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEntityTypeFilter("Client")}
                  className={entityTypeFilter === "Client" ? "bg-teal hover:bg-teal" : ""}
                  data-testid="filter-clients"
                >
                  <User className="w-3 h-3 mr-1" />
                  Clients
                </Button>
                <Button
                  variant={entityTypeFilter === "Counterparty" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEntityTypeFilter("Counterparty")}
                  className={entityTypeFilter === "Counterparty" ? "bg-status-amber hover:bg-status-amber" : ""}
                  data-testid="filter-counterparties"
                >
                  <Users className="w-3 h-3 mr-1" />
                  Counterparties
                </Button>
              </div>

              {/* Help Text */}
              {!searchQuery && !clientName && !otherSideNames && (
                <div className="flex items-start gap-2 p-3 bg-navy-hover/60 rounded-md text-sm text-txt-muted">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Run a conflict check before opening a new matter</p>
                    <p className="text-slate-500 mt-1">
                      Enter the client's name above and the other side/counterparty below.
                      Both will be searched and results labelled accordingly.
                      Separate multiple names with a semicolon (;) e.g. "John Smith; Jane Doe".
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 glass-card rounded-md border border-red-200 bg-status-red-dim/80 py-4 px-5" data-testid="search-error-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-status-red flex-1">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={triggerSearch}
                  disabled={loading || !searchQuery.trim()}
                  className="text-xs border-red-300 text-status-red hover:bg-status-red-dim"
                  data-testid="retry-search-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry
                </Button>
                {(error.includes("authentication") || error.includes("credential") || error.includes("expired")) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/conflicts/reset-client`);
                        toast.info("SharePoint client reset — retrying search...");
                        setTimeout(() => triggerSearch(), 500);
                      } catch {
                        toast.error("Failed to reset client");
                      }
                    }}
                    className="text-xs border-amber-300 text-status-amber hover:bg-status-amber-dim"
                    data-testid="reset-retry-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Reset &amp; Retry
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions: N/A and Not Ready — always available */}
        {!savedConflictCheck && (
          <div className="mb-6 glass-card rounded-md py-3 px-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-txt-muted">
                <span className="font-medium text-white">No search needed?</span> Record a conflict check outcome directly:
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setClearanceType("na"); setShowClearanceForm(true); }}
                  className="text-xs border-blue-500/40 text-blue-700 hover:bg-blue-900/30"
                  data-testid="conflict-na-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  N/A — Advice Only
                </Button>
                {!completingDeferred && <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setClearanceType("not_ready"); setShowClearanceForm(true); }}
                  className="text-xs border-amber-500/50 text-status-amber hover:bg-status-amber-dim/30"
                  data-testid="conflict-not-ready-btn"
                >
                  <Clock className="w-3.5 h-3.5 mr-1.5" />
                  Not Ready — Deferred
                </Button>}
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className={`glass-card rounded-md py-4 px-5 ${results.has_potential_conflict ? "border-amber-500/40 bg-status-amber-dim/30/80" : "border-teal/40 bg-teal-dim/30/80"}`}>
              <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {results.has_potential_conflict ? (
                      <AlertTriangle className="w-6 h-6 text-status-amber" />
                    ) : (
                      <CheckCircle2 className="w-6 h-6 text-teal" />
                    )}
                    <div>
                      <p className={`font-semibold ${results.has_potential_conflict ? "text-status-amber" : "text-teal"}`}>
                        {results.has_potential_conflict 
                          ? `${results.total_matches} potential match${results.total_matches !== 1 ? "es" : ""} found`
                          : "No conflicts found"
                        }
                      </p>
                      <p className="text-sm text-txt-muted">
                        Search: "{results.query}"
                        {results.entity_type_filter && ` (${results.entity_type_filter}s only)`}
                      </p>
                      {/* Party breakdown */}
                      {results.results && results.results.some(r => r.searched_as && r.searched_as !== "unknown") && (
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const clientHits = results.results.filter(r => r.searched_as === "client").length;
                            const otherHits = results.results.filter(r => r.searched_as === "other_side").length;
                            return (
                              <>
                                {clientHits > 0 && (
                                  <span className="text-xs text-teal">{clientHits} from client search</span>
                                )}
                                {clientHits > 0 && otherHits > 0 && <span className="text-xs text-slate-500">|</span>}
                                {otherHits > 0 && (
                                  <span className="text-xs text-status-amber">{otherHits} from other side search</span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  {!results.has_potential_conflict && !savedConflictCheck && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => { setClearanceType("clear"); setShowClearanceForm(true); setTimeout(() => clearanceFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }}
                        className="bg-teal hover:bg-teal"
                        data-testid="record-clearance-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Record Clearance
                      </Button>
                    </div>
                  )}
                  {results.has_potential_conflict && !savedConflictCheck && (
                    <div className="flex items-center gap-3">
                      {!matchReviewed ? (
                        <span className="text-xs text-status-amber animate-pulse">Scroll down and review all matches to enable approval</span>
                      ) : null}
                      <Button
                        onClick={() => { setClearanceType("clear_with_conditions"); setShowClearanceForm(true); setTimeout(() => clearanceFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }}
                        variant="outline"
                        className="border-amber-500/50 text-status-amber hover:bg-status-amber-dim/30"
                        disabled={!matchReviewed}
                        data-testid="record-clearance-with-matches-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {canApproveConflicts ? "Review & Clear (director)" : "Submit for Approval"}
                      </Button>
                    </div>
                  )}
                  {savedConflictCheck && (
                    <Badge className="bg-teal-dim text-teal border-teal">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Saved
                    </Badge>
                  )}
              </div>
            </div>

            {/* Detailed Results */}
            {results.total_matches > 0 && (
              <div className="rounded-md border-2 border-amber-500/60 bg-status-amber-dim/30 shadow-lg shadow-amber-900/20">
                <div className="p-5 border-b border-amber-500/40 bg-status-amber-dim/20">
                  <h2 className="text-lg font-bold text-status-amber flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-status-amber" />
                    Potential Conflict — Review Required
                  </h2>
                  <p className="text-xs text-status-amber/80 mt-1">You must review the match details below before recording a clearance decision.</p>
                </div>
                <div className="p-5">
                  <div className="space-y-3">
                    {results.results.map((match, index) => (
                      <div 
                        key={match.id || index}
                        className="p-4 bg-status-amber-dim/20 rounded-md border border-amber-500/40"
                        data-testid={`match-result-${index}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getEntityIcon(match.entity_type)}
                              <span className="font-semibold text-white truncate">{match.name}</span>
                              <Badge 
                                variant="outline" 
                                className={match.entity_type === "Client" 
                                  ? "bg-teal-dim/30 text-teal border-teal" 
                                  : "bg-status-amber-dim/30 text-status-amber border-amber-500/40"
                                }
                              >
                                {match.entity_type}
                              </Badge>
                              {match.searched_as && match.searched_as !== "unknown" && (
                                <Badge 
                                  variant="outline"
                                  className={match.searched_as === "client"
                                    ? "bg-teal-dim/40 text-teal border-teal/50 text-[10px]"
                                    : "bg-status-amber-dim/40 text-status-amber border-amber-500/50 text-[10px]"
                                  }
                                  data-testid={`searched-as-badge-${index}`}
                                >
                                  {match.searched_as === "client" ? "Client Search" : "Other Side Search"}
                                </Badge>
                              )}
                              {match.client_type && (
                                <Badge variant="outline" className="text-xs">
                                  {getClientTypeIcon(match.client_type)}
                                  <span className="ml-1">{match.client_type}</span>
                                </Badge>
                              )}
                            </div>
                            
                            <div className="text-sm text-txt-muted space-y-1">
                              {match.matter_reference && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">Ref:</span>
                                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">
                                    {match.matter_reference}
                                  </code>
                                </div>
                              )}
                              {match.matter_title && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">Matter:</span>
                                  <span className="truncate">{match.matter_title}</span>
                                </div>
                              )}
                              {match.client_address && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">Address:</span>
                                  <span className="truncate">{match.client_address}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end gap-1">
                            <div className="text-xs text-slate-400">
                              Matched: {match.match_fields.join(", ")}
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              Score: {match.score}
                            </Badge>
                          </div>
                        </div>
                        {/* Issue #2959: confirm a client-record match is the client themselves. */}
                        {!savedConflictCheck && match.id && isClientRecordMatch(match) && (() => {
                          const key = String(match.id);
                          const conf = ownClientConfirmations[key] || { checked: false, reason: "" };
                          const short = conf.checked && conf.reason.trim().length < OWN_CLIENT_MIN_REASON;
                          return (
                            <div className="mt-3 pt-3 border-t border-amber-500/30" data-testid={`own-client-confirm-${index}`}>
                              <label className="flex items-center gap-2 text-sm text-txt cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={conf.checked}
                                  onChange={(e) => setOwnClientConfirmations(prev => ({ ...prev, [key]: { ...conf, checked: e.target.checked } }))}
                                  className="w-4 h-4"
                                  data-testid={`own-client-checkbox-${index}`}
                                />
                                This match is my client (the same person or company)
                              </label>
                              {conf.checked && (
                                <div className="mt-2">
                                  <Input
                                    value={conf.reason}
                                    onChange={(e) => setOwnClientConfirmations(prev => ({ ...prev, [key]: { ...conf, reason: e.target.value } }))}
                                    placeholder="Why you are sure, e.g. same client, earlier Estate Planning Review, same address"
                                    className="bg-white text-sm"
                                    data-testid={`own-client-reason-${index}`}
                                  />
                                  {short && (
                                    <p className="text-xs text-status-amber mt-1">Give a reason of at least {OWN_CLIENT_MIN_REASON} characters.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Review acknowledgement — must scroll through all matches to reach this */}
                <div className="px-5 pb-5">
                  <label className="flex items-center gap-3 p-3 rounded-md border border-amber-500/40 bg-status-amber-dim/30 cursor-pointer hover:bg-status-amber-dim/40 transition-colors" data-testid="match-reviewed-checkbox">
                    <input
                      type="checkbox"
                      checked={matchReviewed}
                      onChange={(e) => setMatchReviewed(e.target.checked)}
                      className="w-4 h-4 rounded border-amber-500 accent-amber-500"
                    />
                    <span className="text-sm font-medium text-status-amber">I have reviewed the potential conflict(s) above and wish to proceed with a clearance decision</span>
                  </label>
                  {matchReviewed && !savedConflictCheck && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => { setClearanceType("clear_with_conditions"); setShowClearanceForm(true); setTimeout(() => clearanceFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }}
                        className="bg-status-amber hover:bg-status-amber text-white"
                        data-testid="bottom-submit-for-approval-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {canApproveConflicts ? "Review & Clear (director)" : "Submit for Approval"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BM-H265: filtered matches, always shown, never silently dropped.
                A hit removed by precision filtering stays visible with its
                reason so a director can always see what the search saw. */}
            {results.suppressed_count > 0 && (
              <div className="glass-card rounded-md">
                <div className="p-5 border-b border-navy-border/60">
                  <button
                    type="button"
                    onClick={() => setShowFilteredMatches((v) => !v)}
                    className="flex w-full items-center justify-between text-left"
                  >
                    <h2 className="text-base font-semibold text-white">
                      {results.suppressed_count} match{results.suppressed_count !== 1 ? "es" : ""} filtered out as not genuine
                    </h2>
                    <span className="text-sm text-txt-muted">
                      {showFilteredMatches ? "Hide" : "Show"}
                    </span>
                  </button>
                  <p className="mt-1 text-sm text-txt-muted">
                    These were found in the index but are not genuine candidates
                    for the parties searched. Nothing is discarded: every one is
                    recorded against the matter with its reason.
                  </p>
                </div>
                {showFilteredMatches && (
                  <div className="p-5">
                    <div className="divide-y divide-navy-border/60">
                      {(results.suppressed_matches || []).map((item, index) => (
                        <div key={index} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-medium text-white">{item.name}</p>
                              <p className="text-sm text-txt-muted">
                                {item.suppression_label}
                              </p>
                              {item.matched_party && (
                                <p className="text-xs text-txt-muted">
                                  Compared against: {item.matched_party}
                                </p>
                              )}
                              {item.evidence && item.evidence.cleared_on_matter && (
                                <p className="text-xs text-txt-muted">
                                  Previously cleared on {item.evidence.cleared_on_matter}
                                </p>
                              )}
                            </div>
                            <Badge variant="outline" className="shrink-0">
                              {item.suppression_reason}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* BM-H265: proof the search actually looked. An empty result
                set is only an all clear when the index it searched was
                populated. */}
            {results.scan_evidence && results.scan_evidence.status === "unproven" && (
              <div className="glass-card rounded-md border-amber-500/40 bg-status-amber-dim/30/80 py-4 px-5">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-status-amber" />
                  <div>
                    <p className="font-semibold text-status-amber">
                      This search cannot be treated as an all clear
                    </p>
                    <p className="text-sm text-txt-muted">
                      {results.scan_evidence.reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {results.scan_evidence && results.scan_evidence.status === "proven" && (
              <p className="text-xs text-txt-muted">
                Searched {results.scan_evidence.total_indexed.toLocaleString()} indexed
                parties across {(results.parties_searched || []).length || 1} party name
                {((results.parties_searched || []).length || 1) !== 1 ? "s" : ""}.
                {results.suppressed_count > 0
                  ? ` ${results.suppressed_count} filtered as not genuine.`
                  : ""}
              </p>
            )}

            {/* Summary by Name */}
            {results.summary_by_name && results.summary_by_name.length > 0 && (
              <div className="glass-card rounded-md">
                <div className="p-5 border-b border-navy-border/60">
                  <h2 className="text-base font-semibold text-white">Summary by Name</h2>
                </div>
                <div className="p-5">
                  <div className="divide-y divide-slate-100">
                    {results.summary_by_name.map((summary, index) => (
                      <div key={index} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{summary.name}</span>
                            {summary.entity_types.map((type) => (
                              <Badge 
                                key={type}
                                variant="outline" 
                                className={type === "Client" 
                                  ? "bg-teal-dim/30 text-teal border-teal text-xs" 
                                  : "bg-status-amber-dim/30 text-status-amber border-amber-500/40 text-xs"
                                }
                              >
                                {type}
                              </Badge>
                            ))}
                          </div>
                          <span className="text-sm text-slate-500">
                            {summary.matter_count} matter{summary.matter_count !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Record Clearance Form — works for all 3 outcomes (clear, N/A, not ready) */}
        {showClearanceForm && !savedConflictCheck && (
          <div ref={clearanceFormRef} className={`mb-6 glass-card rounded-md ${clearanceType === "not_ready" ? "border-amber-500/40 bg-status-amber-dim/30/30" : clearanceType === "na" ? "border-blue-200 bg-blue-900/30/30" : "border-blue-200 bg-blue-900/30/30"}`}>
            <div className="p-5 pb-3 border-b border-blue-100/60">
              <h2 className="text-base font-semibold flex items-center gap-2 text-white">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
                {clearanceType === "na" ? "Record N/A — Advice Only" : clearanceType === "not_ready" ? "Record Deferred Conflict Check" : "Record Conflict Clearance"}
                {(clearanceType === "clear" || clearanceType === "clear_with_conditions") && (
                  <Badge
                    className={clearanceType === "clear_with_conditions" ? "bg-status-amber-dim text-status-amber border-amber-500/40" : "bg-teal-dim text-teal border-teal"}
                    data-testid="clearance-decision-label"
                  >
                    Decision: {conflictDecisionLabel(clearanceType)}
                  </Badge>
                )}
              </h2>
              {clearanceType === "na" && (
                <p className="text-xs text-blue-600 mt-1">No other side to check — advice-only matter. OB, Thirdfort and FORF still required.</p>
              )}
              {clearanceType === "not_ready" && (
                <p className="text-xs text-status-amber mt-1">Conflict check deferred (e.g. seller without buyer). Record reason now, run the check later and link to the matter. OB, Thirdfort and FORF still required.</p>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* BM-H259 (3 Sep 2026, Vanessa) — this banner used to read
                  "For existing matters (pre-CaseMinder), enter the matter
                  reference below." The input it referred to was DELETED in
                  June 2026 after the Sir Peter Birkett P0, and the backend
                  now hard-rejects any client-supplied matter_reference with
                  a 422. The instruction outlived its field and was telling
                  fee earners to do the one thing the route forbids. What is
                  actually true is stated instead. Filleting an existing
                  matter is done via Link OB, not here. */}
              {completingDeferred ? (
              <div className="p-3 bg-status-amber-dim/30 rounded-md border border-amber-500/40 text-xs text-status-amber flex items-start gap-2" data-testid="clearance-completing-deferred">
                <Scale className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>This completes the deferred check on {completingDeferred.matter_reference || completingDeferred.provisional_id}. The matter is already open and staged, so only the clearance decision is recorded here.</span>
              </div>
              ) : (
              <div className="p-3 bg-status-amber-dim/30 rounded-md border border-amber-500/40 text-xs text-status-amber flex items-start gap-2">
                <Scale className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>The permanent matter reference is generated automatically from the fee earner and practice area when the conflict check is completed. For a matter that already exists, use Link OB rather than recording a new conflict check here.</span>
              </div>
              )}

              {/* BM-H3722 (5 Oct 2026, Vanessa): this form used to ask for the
                  client name a second time on N/A and deferred checks (a
                  "Client Name *" box bound to the search text), although the
                  name had already been entered at the top of the page and the
                  save sends that one. The second box is gone; the name is
                  shown here so the person saving can see what will be used. */}
              {(clearanceType === "na" || clearanceType === "not_ready") && (
                <div>
                  <label className="text-sm font-medium text-txt-muted mb-1 block">Client Name</label>
                  <div className="px-3 py-2 rounded-md bg-navy-hover/60 border border-navy-border text-sm text-white" data-testid="clearance-client-name-echo">
                    {clientName.trim() || <span className="text-status-red">Not set. Enter the client name at the top of the page.</span>}
                  </div>
                </div>
              )}

              {/* Not Ready: Reason */}
              {clearanceType === "not_ready" && (
                <div>
                  <label className="text-sm font-medium text-status-amber mb-1 block">Reason for Deferral *</label>
                  <Textarea
                    value={notReadyReason}
                    onChange={(e) => setNotReadyReason(e.target.value)}
                    placeholder="e.g. Seller instructing on residential sale, no buyer yet — will run conflict check when MOS received"
                    className="bg-white border-amber-500/40 focus:ring-amber-400"
                    data-testid="not-ready-reason"
                  />
                </div>
              )}

              {/* BM-H24 (3 Jul 2026, Vanessa) — the "Matter Reference
                  (optional — for existing matters)" input has been REMOVED.
                  The canonical rule (V, locked 18-Jun-2026, Sir Peter
                  Birkett incident) is that references are minted ONLY by the
                  SharePoint poll inside POST /conflict-checks. Any value the
                  user typed here was hard-rejected by the backend with a 422
                  "matter_reference_not_permitted" — which fee earners read as
                  a failed save, so they retried and minted duplicate matter
                  folders. The field's own "for existing matters" label
                  invited exactly the input the backend forbids. Filleting an
                  existing matter is done via Link OB, not here. The input is
                  gone and the POST no longer sends matter_reference. */}
              {/* 14May2026: FE + PA are now collected upfront (above Client
                  Name). The duplicate selects that used to live here were
                  removed — the upfront values are reused at submit time.
                  A read-only echo is shown so the supervisor can see what
                  was picked when reviewing the clearance form. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-txt-muted mb-1 block">Fee Earner</label>
                  <div className="px-3 py-2 rounded-md bg-navy-hover/60 border border-navy-border text-sm text-white" data-testid="clearance-fee-earner-echo">
                    {feeEarner || <span className="text-status-red">— not set; go back and pick a fee earner</span>}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-txt-muted mb-1 block">Practice Area</label>
                  <div className="px-3 py-2 rounded-md bg-navy-hover/60 border border-navy-border text-sm text-white" data-testid="clearance-practice-area-echo">
                    {practiceArea || <span className="text-status-red">— not set; go back and pick a practice area</span>}
                  </div>
                </div>
              </div>
              {/* BM-H3772 (6 Oct 2026, Vanessa): completing a deferred check
                  used to ask again for the client type, decision maker and
                  matter title, "as if it's a file opening conflict". They
                  were recorded when the matter was deferred and staged, so
                  they are shown, and the save sends the recorded values. */}
              {completingDeferred && clientType && briefDescription.trim() && decisionMaker ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="clearance-deferred-details-echo">
                  {[["Client is a", clientType], ["Decision Maker", decisionMaker], ["Matter title", briefDescription]].map(([label, value]) => (
                    <div key={label}>
                      <label className="text-sm font-medium text-txt-muted mb-1 block">{label}</label>
                      <div className="px-3 py-2 rounded-md bg-navy-hover/60 border border-navy-border text-sm text-white capitalize">{value}</div>
                    </div>
                  ))}
                </div>
              ) : (<>
              <div>
                <label className="text-sm font-medium text-txt mb-1 block">Client is a *</label>
                <Select value={clientType} onValueChange={setClientType}>
                  <SelectTrigger data-testid="clearance-client-type">
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="llp">LLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-txt mb-1 block">Decision Maker *</label>
                <Select value={decisionMaker} onValueChange={setDecisionMaker}>
                  <SelectTrigger data-testid="clearance-decision-maker">
                    <SelectValue placeholder="Select decision maker" />
                  </SelectTrigger>
                  <SelectContent>
                    {DECISION_MAKERS.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-txt mb-1 block">Brief description / matter title *</label>
                <Input
                  value={briefDescription}
                  onChange={(e) => setBriefDescription(e.target.value)}
                  placeholder="e.g. Sale of 14 Cornmill Lane"
                  className="bg-white"
                  data-testid="clearance-brief-description"
                />
                <p className="text-xs text-txt-muted mt-1">
                  Used in the auto-generated SharePoint folder name.
                </p>
              </div>
              </>)}
              <div>
                <label className="text-sm font-medium text-txt mb-1 block">Notes</label>
                <Textarea
                  value={decisionNotes}
                  onChange={(e) => setDecisionNotes(e.target.value)}
                  placeholder="Any additional notes about this clearance..."
                  className="bg-white"
                  data-testid="clearance-notes"
                />
              </div>
              {clearanceType !== "not_ready" && uncoveredJoint.length > 0 && (
                <div
                  className="rounded-md border border-status-amber bg-status-amber/10 p-3 text-sm text-txt"
                  data-testid="joint-coverage-warning"
                >
                  {uncoveredMessage(uncoveredJoint)}
                </div>
              )}
              {directorApprovalPath && (
                <div data-testid="director-approval-reason">
                  {/* BM-H4514: the director says which it is; nothing is pre-chosen. */}
                  <div className="flex gap-2 mb-2" role="radiogroup" aria-label="Your decision" data-testid="director-outcome">
                    {[
                      ["no_conflict", "No conflict", CheckCircle2],
                      ["with_conditions", "With conditions", AlertTriangle],
                    ].map(([value, label, Icon]) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={directorOutcome === value}
                        onClick={() => setDirectorOutcome(value)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm ${directorOutcome === value ? "border-indigo-600 bg-indigo-50 text-indigo-800 font-medium" : "border-slate-300 bg-white text-txt"}`}
                        data-testid={`director-outcome-${value}`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                  <label className="text-sm font-medium text-txt mb-1 block">
                    {directorOutcome === "with_conditions"
                      ? "The conditions you are clearing it on"
                      : "Your reason (you are approving it as a director)"}
                  </label>
                  <Textarea
                    value={directorReason}
                    onChange={(e) => setDirectorReason(e.target.value)}
                    placeholder="e.g. The other party's matter closed in 2024 and is unrelated; no confidential information is at risk"
                    className="bg-white"
                    data-testid="director-approval-reason-input"
                  />
                  {directorReason.trim().length < DIRECTOR_MIN_REASON && (
                    <p className="text-xs text-txt-muted mt-1">At least {DIRECTOR_MIN_REASON} characters. This is recorded as your decision.</p>
                  )}
                </div>
              )}
              {potentialConflictPath && allOwnClientConfirmed && (
                <p className="text-sm text-teal" data-testid="own-client-all-confirmed">
                  Every match is confirmed as your client's own record, so this clears without supervisor approval. Your reasons are recorded.
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveClearance}
                  disabled={savingClearance || saveBlockers.length > 0}
                  className={clearanceType === "not_ready" ? "bg-status-amber hover:bg-status-amber" : "bg-teal hover:bg-teal"}
                  data-testid="save-clearance-btn"
                >
                  {savingClearance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {clearanceType === "na" ? "Save N/A & Stage" : clearanceType === "not_ready" ? "Save Deferred & Stage" : results?.has_potential_conflict ? (allOwnClientConfirmed ? "Save & Clear (client's own record)" : directorApprovalPath ? "Approve & Clear (director)" : "Submit for Supervisor Approval") : "Save & Stage to SharePoint"}
                </Button>
                <Button variant="outline" onClick={() => setShowClearanceForm(false)}>Cancel</Button>
              </div>
              {saveBlockers.length > 0 && (
                <ul className="text-xs text-status-amber list-disc pl-5 space-y-0.5" data-testid="save-clearance-blockers">
                  {saveBlockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Next Steps — after clearance saved */}
        {savedConflictCheck && (
          <NextStepsPanel
            savedConflictCheck={savedConflictCheck}
            setSavedConflictCheck={setSavedConflictCheck}
            navigate={navigate}
            initialClientEmail={quoteClientEmail}
            initialMatterDescription={quoteMatterDescription}
            onReset={() => {
              setSearchQuery("");
              setClientName("");
              // BM-H616: the next check is not from this quote. Left set, they
              // went out as source_quote_ref / estimated_fee on its save.
              setSourceQuoteRef("");
              setEstimatedFee("");
              setOtherSideNames("");
              setResults(null);
              setError(null);
              setSavedConflictCheck(null);
              setShowClearanceForm(false);
              setClearanceType(null);
              setMatchReviewed(false);
              setFeeEarner("");
              setPracticeArea("");
              setClientType("");
              setBriefDescription("");
              // BM-H615: the next check is not from this quote.
              setQuoteClientEmail("");
              setQuoteMatterDescription("");
            }}
          />
        )}
        {/* Index Status Warning */}
        {indexStatus && !indexStatus.configured && (
          <div className="mt-6 glass-card rounded-md border-amber-500/40 bg-status-amber-dim/30/80 py-4 px-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-status-amber mt-0.5" />
              <div>
                <p className="font-medium text-status-amber">Conflicts Index Not Configured</p>
                <p className="text-sm text-status-amber mt-1">
                  The SharePoint Client Contacts List has not been configured yet. 
                  Conflict searches will not return results until the list is set up.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
